/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Archive, ArrowLeft, Building2, FileSpreadsheet, CalendarDays, ChevronRight, Copy, Download, Edit3, Filter, MoreVertical, Palette, Plus, Printer, RotateCcw, Search, Trash2, X, FileDown } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './AgendamentosSetorial.css';
import { useAuth } from '../contexts/AuthContext';
import * as XLSX from 'xlsx';

const TITULOS = { medico: 'Médico', odontologia: 'Odontologia' };
const TIPOS = ['Agenda médica', 'Exames e procedimentos', 'Psicologia'];
const CORES_AGENDA = [
  ['', 'Sem cor', 'transparent'],
  ['#e6f6ec', 'Verde', '#4cb98a'],
  ['#fff4d6', 'Amarelo', '#e8a93b'],
  ['#e3f0fb', 'Azul', '#5b9bd5'],
  ['#fde8e8', 'Vermelho', '#e07b7b'],
  ['#efe9fd', 'Roxo', '#9b7fd4'],
];

const CORES_CARD_AGENDA = [
  '#4D56D8', '#2563EB', '#0EA5E9', '#0891B2', '#14B8A6', '#10B981', '#22C55E', '#84CC16',
  '#EAB308', '#F59E0B', '#F97316', '#EF4444', '#DC2626', '#E11D48', '#EC4899', '#D946EF',
  '#A855F7', '#7C3AED', '#64748B', '#334155',
];

const STATUS = [
  ['disponivel', 'Disponível'], ['aguardando', 'Pendente'], ['confirmado', 'Confirmado'],
  ['compareceu', 'Compareceu'], ['faltou', 'Faltou'], ['cancelado', 'Cancelado']
];
const CORES_STATUS_LINHA = {
  aguardando: '#fff4d6',
  confirmado: '#e6f6ec',
  compareceu: '#e6f6ec',
  faltou: '#fde8e8',
  cancelado: '#edf0f2',
};
const CAMPOS = [
  ['horario', 'Horário', 'time'], ['paciente', 'Paciente'], ['contato', 'Contato'],
  ['observacao', 'Observação'], ['pagamento', 'Pagamento'], ['checkin', 'Check-in'],
  ['pos_consulta', 'Pós-consulta']
];
const PAD = (n) => String(n).padStart(2, '0');
const MES_ATUAL = () => { const d = new Date(); return `${d.getFullYear()}-${PAD(d.getMonth() + 1)}`; };
const HOJE_ISO = () => { const d = new Date(); return `${d.getFullYear()}-${PAD(d.getMonth() + 1)}-${PAD(d.getDate())}`; };
const PRIMEIRO_DIA = (mes) => `${mes}-01`;
const ULTIMO_DIA = (mes) => { const [a, m] = mes.split('-').map(Number); return `${a}-${PAD(m)}-${PAD(new Date(a, m, 0).getDate())}`; };
const ROTULO_MES = (mes) => { const [a, m] = mes.split('-').map(Number); return new Date(a, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }); };
const ROTULO_ABA_MES = (mes) => ROTULO_MES(mes).replace(' de ', ' ');
const isoParaBR = (valor) => {
  const m = TEXTO(valor).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};
const brParaISO = (valor) => {
  const m = TEXTO(valor).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return '';
  const [, dd, mm, yyyy] = m;
  const d = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)));
  return d.getUTCFullYear() === Number(yyyy) && d.getUTCMonth() === Number(mm) - 1 && d.getUTCDate() === Number(dd)
    ? `${yyyy}-${mm}-${dd}` : '';
};
function DateInputBR({ value, onChange, required = false }) {
  const [texto, setTexto] = useState(isoParaBR(value));
  const seletor = useRef(null);
  useEffect(() => { setTexto(isoParaBR(value)); }, [value]);
  const alterar = (bruto) => {
    const digitos = bruto.replace(/\D/g, '').slice(0, 8);
    const exibido = digitos.length <= 2 ? digitos : digitos.length <= 4
      ? `${digitos.slice(0, 2)}/${digitos.slice(2)}`
      : `${digitos.slice(0, 2)}/${digitos.slice(2, 4)}/${digitos.slice(4)}`;
    setTexto(exibido);
    if (!exibido) onChange('');
    else { const iso = brParaISO(exibido); if (iso) onChange(iso); }
  };
  return <span className="agset-date-br">
    <input type="text" inputMode="numeric" autoComplete="off" placeholder="DD/MM/AAAA" aria-label="Data (dia/mês/ano)" value={texto} required={required} onChange={(e) => alterar(e.target.value)} onBlur={() => { if (texto && !brParaISO(texto)) setTexto(isoParaBR(value)); }} />
    <button type="button" className="agset-date-br-picker" title="Escolher data" aria-label="Escolher data" onClick={() => { if (seletor.current?.showPicker) seletor.current.showPicker(); else seletor.current?.click(); }}><CalendarDays size={14} /></button>
    <input ref={seletor} className="agset-date-native-hidden" type="date" lang="pt-BR" value={value || ''} onChange={(e) => onChange(e.target.value)} tabIndex={-1} aria-hidden="true" />
  </span>;
}
function MonthPickerBR({ value, onChange, compact = false, onExcluir, onRenomear, podeExcluir = true }) {
  const [aberto, setAberto] = useState(false);
  const [modo, setModo] = useState(compact && onExcluir ? 'menu' : 'adicionar');
  const [pos, setPos] = useState(null);
  const [ano, setAno] = useState(Number(TEXTO(value).slice(0, 4)) || new Date().getFullYear());
  useEffect(() => { if (value) setAno(Number(value.slice(0, 4))); }, [value]);
  const mesesPt = Array.from({ length: 12 }, (_, i) => new Date(ano, i, 1).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', ''));
  const rotulo = ROTULO_MES(value).replace(/^./, (letra) => letra.toLocaleUpperCase('pt-BR'));
  return <span className={`agset-monthpicker ${compact ? 'compact' : ''}`}>
    <button type="button" className="agset-monthpicker-trigger" onClick={(e) => { if (compact) { const r = e.currentTarget.getBoundingClientRect(); setPos({ left: r.left, bottom: window.innerHeight - r.top + 7 }); } setModo(compact && onExcluir ? 'menu' : 'adicionar'); setAberto((aberto) => !aberto); }} aria-expanded={aberto}>
      {compact ? <Plus size={17} /> : <><CalendarDays size={14} /><span>{rotulo}</span></>}
    </button>
    {aberto && <span className="agset-monthpicker-popover" style={compact && pos ? { position: 'fixed', left: pos.left, bottom: pos.bottom, top: 'auto', right: 'auto', zIndex: 2000 } : undefined} role="dialog" aria-label="Escolher mês">{modo === 'menu' ? <span className="agset-tab-menu"><button type="button" onClick={() => setModo('adicionar')}>Adicionar mês</button>{onRenomear && <button type="button" onClick={() => { setAberto(false); onRenomear(); }}>Renomear aba</button>}<button type="button" className="danger" disabled={!podeExcluir} onClick={() => { setAberto(false); onExcluir(); }}>Excluir aba</button></span> : <>
      <span className="agset-monthpicker-year"><button type="button" aria-label="Ano anterior" onClick={() => setAno((n) => n - 1)}>‹</button><strong>{ano}</strong><button type="button" aria-label="Próximo ano" onClick={() => setAno((n) => n + 1)}>›</button></span>
      <span className="agset-monthpicker-grid">{mesesPt.map((nome, i) => <button type="button" key={i} className={value === `${ano}-${PAD(i + 1)}` ? 'selected' : ''} onClick={() => { onChange(`${ano}-${PAD(i + 1)}`); setAberto(false); }}>{nome}</button>)}</span>
    </>}</span>}
  </span>;
}
function DayPickerBR({ value, onChange, diasComAgenda = [] }) {
  const [aberto, setAberto] = useState(false);
  const base = /^\d{4}-\d{2}/.test(TEXTO(value)) ? TEXTO(value).slice(0, 7) : MES_ATUAL();
  const [visivel, setVisivel] = useState(base);
  useEffect(() => { setVisivel(base); }, [base]);
  const [ano, mesNum] = visivel.split('-').map(Number);
  const deslocamento = new Date(ano, mesNum - 1, 1).getDay();
  const totalDias = new Date(ano, mesNum, 0).getDate();
  const celulas = [...Array(deslocamento).fill(null), ...Array.from({ length: totalDias }, (_, i) => i + 1)];
  const navegar = (delta) => { const d = new Date(ano, mesNum - 1 + delta, 1); setVisivel(`${d.getFullYear()}-${PAD(d.getMonth() + 1)}`); };
  const rotuloMes = ROTULO_MES(visivel).replace(/^./, (l) => l.toLocaleUpperCase('pt-BR'));
  const comAgenda = new Set(diasComAgenda);
  return <span className="agset-monthpicker agset-daypicker">
    <button type="button" className="agset-monthpicker-trigger" onClick={() => setAberto((a) => !a)} aria-expanded={aberto}><CalendarDays size={14} /><span>{isoParaBR(value) || 'Escolher dia'}</span></button>
    {aberto && <span className="agset-monthpicker-popover agset-daypicker-popover" role="dialog" aria-label="Escolher dia">
      <span className="agset-monthpicker-year"><button type="button" aria-label="Mês anterior" onClick={() => navegar(-1)}>‹</button><strong>{rotuloMes}</strong><button type="button" aria-label="Próximo mês" onClick={() => navegar(1)}>›</button></span>
      <span className="agset-daypicker-week">{['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((l, i) => <i key={i}>{l}</i>)}</span>
      <span className="agset-daypicker-grid">{celulas.map((dia, i) => {
        if (!dia) return <span key={`v${i}`} />;
        const iso = `${visivel}-${PAD(dia)}`;
        return <button type="button" key={iso} className={`${iso === value ? 'selected' : ''} ${comAgenda.has(iso) ? 'has-agenda' : ''}`} onClick={() => { onChange(iso); setAberto(false); }}>{dia}</button>;
      })}</span>
    </span>}
  </span>;
}
const SOMA_DIAS = (iso, quantidade) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + quantidade); return `${d.getFullYear()}-${PAD(d.getMonth() + 1)}-${PAD(d.getDate())}`; };
const TEXTO = (v) => String(v == null ? '' : v);
const MASCARA_TEL = (v) => {
  const s = TEXTO(v);
  if (/[A-Za-z]/.test(s)) return s;
  let d = s.replace(/\D/g, '');
  if (d.length >= 12 && d.startsWith('55')) d = d.slice(2);
  d = d.slice(0, 11);
  if (!d) return '';
  if (d.length <= 2) return `(${d}`;
  const ddd = d.slice(0, 2);
  const resto = d.slice(2);
  if (d.length === 11) return `(${ddd})${resto.slice(0, 5)}-${resto.slice(5)}`;
  return resto.length > 4 ? `(${ddd})${resto.slice(0, 4)}-${resto.slice(4)}` : `(${ddd})${resto}`;
};
const FINAL_TEL = (v) => {
  const s = TEXTO(v);
  if (!s.trim() || /[A-Za-z]/.test(s)) return s;
  let d = s.replace(/\D/g, '');
  if (d.length >= 12 && d.startsWith('55')) d = d.slice(2);
  return d.length === 10 || d.length === 11 ? MASCARA_TEL(s) : s;
};
const ARR = (v) => { if (Array.isArray(v)) return v; if (typeof v === 'string') { try { const parsed = JSON.parse(v); return Array.isArray(parsed) ? parsed : []; } catch (_) { return []; } } return []; };
const OBJ = (v) => { if (v && typeof v === 'object' && !Array.isArray(v)) return v; if (typeof v === 'string') { try { const parsed = JSON.parse(v); return parsed && typeof parsed === 'object' ? parsed : {}; } catch (_) { return {}; } } return {}; };
const CHAVE_AGENDA = (a) => [
  TEXTO(a.medicoId ?? a.medico_id ?? a.medico_nome ?? a.nome).trim().toLowerCase(),
  TEXTO(a.especialidade).trim().toLowerCase(),
].join('::');

async function api(url, options = {}) {
  const response = await fetch(`/api${url}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  });
  const text = await response.text();
  let data = {};
  if (text) { try { data = JSON.parse(text); } catch (_) { data = { error: text.slice(0, 200) }; } }
  if (!response.ok) throw new Error(data.error || `Erro HTTP ${response.status}`);
  return data;
}

const MESES_OCULTOS = (setor) => { try { const v = JSON.parse(localStorage.getItem(`agendamentos-meses-ocultos-${setor}`) || '[]'); return Array.isArray(v) ? v : []; } catch (_) { return []; } };
function iniciarMeses(setor) {
  const ocultos = MESES_OCULTOS(setor);
  const lista = iniciarMesesBase(setor).filter((m) => !ocultos.includes(m));
  return lista.length ? lista : [MES_ATUAL()];
}
function iniciarMesesBase(setor) {
  const atual = MES_ATUAL();
  const recentes = Array.from({ length: 4 }, (_, indice) => {
    const data = new Date();
    data.setDate(1);
    data.setMonth(data.getMonth() - indice);
    return `${data.getFullYear()}-${PAD(data.getMonth() + 1)}`;
  });
  try {
    const salvos = JSON.parse(localStorage.getItem(`agendamentos-meses-${setor}`) || '[]');
    return Array.from(new Set([atual, ...recentes, ...salvos.filter((m) => /^\d{4}-\d{2}$/.test(m))])).sort().reverse();
  } catch (_) { return Array.from(new Set([atual, ...recentes])).sort().reverse(); }
}

const MASCARA_HORA = (valor) => {
  const d = String(valor ?? '').replace(/\D/g, '').slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}:${d.slice(2)}` : d;
};
const NORMALIZA_HORA = (valor, anterior) => {
  const d = String(valor ?? '').replace(/\D/g, '').slice(0, 4);
  let h; let m;
  if (d.length === 1 || d.length === 2) { h = Number(d); m = 0; }
  else if (d.length === 3) { h = Number(d.slice(0, 1)); m = Number(d.slice(1)); }
  else if (d.length === 4) { h = Number(d.slice(0, 2)); m = Number(d.slice(2)); }
  else return anterior ?? '';
  if (!(h >= 0 && h <= 23 && m >= 0 && m <= 59)) return anterior ?? '';
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};
function proximoHorario(ultimo, intervalo, inicio) {
  const horario = /^\d{2}:\d{2}$/.test(TEXTO(ultimo)) ? ultimo : (inicio || '09:00');
  const [h, m] = horario.split(':').map(Number);
  const total = h * 60 + m + (Number(intervalo) || 20);
  if (total >= 1440) return horario;
  return `${PAD(Math.floor(total / 60))}:${PAD(total % 60)}`;
}
function minutosHorario(valor) {
  const texto = TEXTO(valor).trim();
  const match = texto.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
  if (!match) return Number.POSITIVE_INFINITY;
  let hora = Number(match[1]);
  const minuto = Number(match[2]);
  if (minuto > 59 || hora > 23) return Number.POSITIVE_INFINITY;
  if (match[3]) {
    if (hora < 1 || hora > 12) return Number.POSITIVE_INFINITY;
    hora = hora % 12 + (match[3].toUpperCase() === 'PM' ? 12 : 0);
  }
  return hora * 60 + minuto;
}

// Nome da coluna conforme o setor: na Odontologia, "Observação" aparece como "Tratamento".
const rotuloCampo = (campo, rotulo, setor) => (setor === 'odontologia' && campo === 'observacao' ? 'Tratamento' : rotulo);

// Status editável com sugestões (igual ao Checklist): escolhe da lista ou digita um novo.
function StatusEditavel({ valor, opcoesExtras = [], onFocus, onSalvar, idLista }) {
  const rotuloDe = (v) => (STATUS.find(([chave]) => chave === v) || [])[1] || v || '';
  const atual = valor || 'disponivel';
  const [digitado, setDigitado] = useState(rotuloDe(atual));

  useEffect(() => { setDigitado(rotuloDe(valor || 'disponivel')); }, [valor]); // eslint-disable-line react-hooks/exhaustive-deps

  const opcoes = [...STATUS.map(([, rotulo]) => rotulo), ...opcoesExtras];

  const gravar = (bruto) => {
    const limpo = String(bruto || '').trim();
    if (!limpo) { setDigitado(rotuloDe(atual)); return; }
    const padrao = STATUS.find(([chave, rotulo]) => rotulo.toLowerCase() === limpo.toLowerCase() || chave === limpo.toLowerCase());
    const novo = padrao ? padrao[0] : limpo.toUpperCase();
    setDigitado(rotuloDe(novo));
    if (novo !== atual) onSalvar(novo);
  };

  return (
    <>
      <input
        className="agset-status-input"
        list={idLista}
        value={digitado}
        onFocus={(e) => { if (onFocus) onFocus(); e.target.select(); }}
        onChange={(e) => {
          const v = e.target.value;
          setDigitado(v);
          if (opcoes.some((o) => o.toLowerCase() === v.trim().toLowerCase())) gravar(v);
        }}
        onBlur={(e) => gravar(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
          if (e.key === 'Escape') { setDigitado(rotuloDe(atual)); e.currentTarget.blur(); }
        }}
        aria-label="Status"
      />
      <datalist id={idLista}>{opcoes.map((o) => <option key={o} value={o} />)}</datalist>
    </>
  );
}

function BlocoAgenda({ agenda, setor, profissional, onAtualizar, onDuplicar, onNovaAgenda, mostrarControles, gruposAgendas, grupoSelecionado, onTrocarGrupo, mes, onSelecionarMes, onVoltar, blocosDatas = [], onTrocarBloco, onEscolherDia, onArquivar }) {
  const [linhas, setLinhas] = useState([]);
  const [colunas, setColunas] = useState(ARR(agenda.colunas_extras));
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [dataDuplicar, setDataDuplicar] = useState(SOMA_DIAS(TEXTO(agenda.data).slice(0, 10), 7));
  const [duplicando, setDuplicando] = useState(false);
  const [linhaSelecionadaId, setLinhaSelecionadaId] = useState(null);
  const [linhasSelecionadas, setLinhasSelecionadas] = useState([]);
  const [linhasRecemCriadas, setLinhasRecemCriadas] = useState([]);
  const [clipboard, setClipboard] = useState(null);
  const [buscaLinha, setBuscaLinha] = useState('');
  const [filtrosColuna, setFiltrosColuna] = useState({ checkin: '', pos_consulta: '', status: '' });
  const [maisAberto, setMaisAberto] = useState(false);
  const [corAberta, setCorAberta] = useState(null);
    const [obsInfo, setObsInfo] = useState(TEXTO(agenda.observacoes));
    const [menuAgAberto, setMenuAgAberto] = useState(false);
    const [editandoAg, setEditandoAg] = useState(false);
    const [salvandoAg, setSalvandoAg] = useState(false);
    const [formAg, setFormAg] = useState({ nome: '', associado: '', particular: '' });
    const [nomeProfEditado, setNomeProfEditado] = useState('');
    const [corLinhaModal, setCorLinhaModal] = useState(null);
    const [salvandoCorLinha, setSalvandoCorLinha] = useState(false);
    const tomClaroLinha = (hex) => {
      const h = TEXTO(hex).replace('#', '');
      if (!/^[0-9a-f]{6}$/i.test(h)) return '';
      const mistura = (i) => Math.round(255 - (255 - parseInt(h.slice(i, i + 2), 16)) * 0.2).toString(16).padStart(2, '0');
      return `#${mistura(0)}${mistura(2)}${mistura(4)}`;
    };
    // o botão de paleta da linha continua acionando corAberta; aqui ele vira o modal novo
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      if (!corAberta) return;
      const l = linhas.find((item) => String(item.id) === String(corAberta.id));
      setCorAberta(null);
      if (!l) return;
      const atual = TEXTO(l.cor).toLowerCase();
      const dePaleta = CORES_CARD_AGENDA.find((c) => tomClaroLinha(c) === atual);
      setCorLinhaModal({ id: l.id, original: TEXTO(l.cor), valor: dePaleta || TEXTO(l.cor) });
    }, [corAberta]);
    useEffect(() => {
      if (!corLinhaModal) return undefined;
      const fecharEsc = (ev) => { if (ev.key === 'Escape') setCorLinhaModal(null); };
      document.addEventListener('keydown', fecharEsc);
      return () => document.removeEventListener('keydown', fecharEsc);
    }, [corLinhaModal]);
    const salvarCorLinha = async () => {
      const m = corLinhaModal;
      if (!m) return;
      const l = linhas.find((item) => String(item.id) === String(m.id));
      if (!l || m.valor === m.original) { setCorLinhaModal(null); return; }
      const nova = m.valor ? (tomClaroLinha(m.valor) || m.valor) : '';
      try {
        setSalvandoCorLinha(true);
        await atualizarLinha(l, { cor: nova });
        setCorLinhaModal(null);
      } finally { setSalvandoCorLinha(false); }
    };

    useEffect(() => {
      if (!menuAgAberto) return undefined;
      const fechar = (ev) => { if (!ev.target.closest || !ev.target.closest('.agset-hm-wrap')) setMenuAgAberto(false); };
      document.addEventListener('mousedown', fechar);
      return () => document.removeEventListener('mousedown', fechar);
    }, [menuAgAberto]);
    const abrirEditarAg = () => {
      setMenuAgAberto(false);
      setFormAg({ nome: TEXTO(profissionalNome), associado: TEXTO(agenda.valor_cartao), particular: TEXTO(agenda.valor_particular) });
      setEditandoAg(true);
      abrirObs(false);
    };
    const cancelarEditarAg = () => { setEditandoAg(false); setEditandoObs(false); };
    const salvarEditarAg = async () => {
      const nome = formAg.nome.trim();
      if (!nome) { setErro('Informe o nome do profissional.'); return; }
      const corpo = { valor_cartao: formAg.associado.trim(), valor_particular: formAg.particular.trim() };
      if (nome !== TEXTO(profissionalNome).trim()) {
        corpo.nome = `Agenda - ${nome}`;
        corpo.profissional_nome = nome;
        corpo.especialidade = TEXTO(agenda.especialidade);
      }
      if (editandoObs) corpo.observacoes = rascunhoObs.split('\n').map((s) => s.trim()).filter(Boolean).join('\n');
      try {
        setSalvandoAg(true);
        await api(`/agenda-configuracoes/${agenda.id}`, { method: 'PUT', body: JSON.stringify(corpo) });
        if (corpo.observacoes !== undefined) setObsInfo(corpo.observacoes);
        if (corpo.profissional_nome) setNomeProfEditado(nome);
        setEditandoObs(false);
        setEditandoAg(false);
        setErro('');
        if (onAtualizar) await onAtualizar();
      } catch (e) { setErro(`Não foi possível salvar a agenda: ${e.message}`); }
      finally { setSalvandoAg(false); }
    };
    const arquivarDiaAg = async () => {
      setMenuAgAberto(false);
      const dia = TEXTO(agenda.data).slice(0, 10).split('-').reverse().join('/');
      if (!window.confirm(`Arquivar a agenda de ${dia}? Os horários serão preservados. Os outros dias não são afetados.`)) return;
      try {
        await api(`/agenda-configuracoes/${agenda.id}/estado`, { method: 'POST', body: JSON.stringify({ estado: 'arquivada' }) });
        if (onAtualizar) await onAtualizar();
        if (onVoltar) onVoltar();
      } catch (e) { setErro(`Não foi possível arquivar a agenda: ${e.message}`); }
    };
    const excluirAg = async () => {
      setMenuAgAberto(false);
      if (carregando) { setErro('Aguarde o carregamento dos horários e tente de novo.'); return; }
      const qtd = linhas.filter((l) => TEXTO(l.paciente).trim()).length;
      const dia = TEXTO(agenda.data).slice(0, 10).split('-').reverse().join('/');
      const msg = qtd > 0
        ? `Esta agenda de ${dia} tem ${qtd} cliente${qtd === 1 ? '' : 's'} agendado${qtd === 1 ? '' : 's'}.\n\nDeseja realmente excluir? Os agendamentos também serão apagados.`
        : `Excluir definitivamente a agenda de ${dia}? Os horários também serão apagados.`;
      if (!window.confirm(msg)) return;
      try {
        await api(`/agenda-configuracoes/${agenda.id}`, { method: 'DELETE' });
        if (onAtualizar) await onAtualizar();
        if (onVoltar) onVoltar();
      } catch (e) { setErro(`Não foi possível excluir a agenda: ${e.message}`); }
    };

    const [editandoObs, setEditandoObs] = useState(false);
    const [rascunhoObs, setRascunhoObs] = useState('');
    useEffect(() => { setObsInfo(TEXTO(agenda.observacoes)); setEditandoObs(false); }, [agenda.id, agenda.observacoes]);
    const abrirObs = (adicionar) => { setRascunhoObs(adicionar ? (obsInfo ? obsInfo + '\n' : '') : obsInfo); setEditandoObs(true); };
    const salvarObs = async () => {
      const novo = rascunhoObs.split('\n').map((s) => s.trim()).filter(Boolean).join('\n');
      try {
        await api(`/agenda-configuracoes/${agenda.id}`, { method: 'PUT', body: JSON.stringify({ observacoes: novo }) });
        setObsInfo(novo);
        setEditandoObs(false);
        setErro('');
      } catch (e) { setErro(e.message); }
    };
  const [larg, setLarg] = useState(() => { try { const v = JSON.parse(localStorage.getItem('agset-larg-agenda')); return v && typeof v === 'object' ? v : {}; } catch (_) { return {}; } });
  const largRef = useRef(larg);
  const headRef = useRef(null);

  useEffect(() => {
    if (!corAberta) return undefined;
    const fecharFora = (evento) => {
      const alvo = evento.target;
      if (alvo && typeof alvo.closest === 'function'
        && (alvo.closest('.agset-color-popover') || alvo.closest('.agset-color-trigger'))) return;
      setCorAberta(null);
    };
    const fecharEsc = (evento) => { if (evento.key === 'Escape') setCorAberta(null); };
    const fecharAoRolar = () => setCorAberta(null);
    document.addEventListener('pointerdown', fecharFora);
    document.addEventListener('keydown', fecharEsc);
    window.addEventListener('scroll', fecharAoRolar, true);
    return () => {
      document.removeEventListener('pointerdown', fecharFora);
      document.removeEventListener('keydown', fecharEsc);
      window.removeEventListener('scroll', fecharAoRolar, true);
    };
  }, [corAberta]);

  const carregarLinhas = useCallback(async () => {
    try {
      setCarregando(true);
      const dados = await api(`/agenda-configuracoes/${agenda.id}/horarios`);
      setLinhas(Array.isArray(dados) ? dados : []);
      setErro('');
    } catch (e) { setErro(e.message); }
    finally { setCarregando(false); }
  }, [agenda.id]);

  useEffect(() => { carregarLinhas(); }, [carregarLinhas]);
  useEffect(() => { setColunas(ARR(agenda.colunas_extras)); }, [agenda.colunas_extras]);

  const atualizarLinha = async (linha, campos) => {
    const anteriores = linhas;

    const camposTexto = new Set([
      'paciente',
      'contato',
      'observacao',
      'pagamento',
      'checkin',
      'pos_consulta'
    ]);

    const camposNormalizados = Object.fromEntries(
      Object.entries(campos).map(([campo, valor]) => {
        if (camposTexto.has(campo) && typeof valor === 'string') {
          return [campo, valor.toLocaleUpperCase('pt-BR')];
        }

        if (
          campo === 'dados_extras' &&
          valor &&
          typeof valor === 'object' &&
          !Array.isArray(valor)
        ) {
          return [
            campo,
            Object.fromEntries(
              Object.entries(valor).map(([chave, item]) => [
                chave,
                typeof item === 'string'
                  ? item.toLocaleUpperCase('pt-BR')
                  : item
              ])
            )
          ];
        }

        return [campo, valor];
      })
    );

    setLinhas((atuais) => atuais.map((l) => (
      l.id === linha.id
        ? { ...l, ...camposNormalizados }
        : l
    )));

    try {
      const salva = await api(`/agenda-horarios/${linha.id}`, {
        method: 'PUT',
        body: JSON.stringify(camposNormalizados)
      });

      setLinhas((atuais) => atuais.map((l) => (
        l.id === linha.id
          ? { ...l, ...camposNormalizados, ...salva }
          : l
      )));
    } catch (e) {
      setLinhas((atuais) => atuais.map((l) => (
        l.id === linha.id
          ? {
              ...l,
              ...Object.fromEntries(
                Object.keys(camposNormalizados).map((campo) => [
                  campo,
                  anteriores.find((a) => a.id === linha.id)?.[campo]
                ])
              )
            }
          : l
      )));
      setErro(e.message);
    }
  };

  const abrirPaletaCor = (evento, linha) => {
    evento.preventDefault();
    evento.stopPropagation();
    const r = evento.currentTarget.getBoundingClientRect();
    const largura = 170;
    const altura = 238;
    const abaixo = window.innerHeight - r.bottom;
    const x = Math.max(8, Math.min(r.right - largura, window.innerWidth - largura - 8));
    const y = abaixo >= altura + 8
      ? r.bottom + 4
      : Math.max(8, r.top - altura - 4);
    setCorAberta((atual) => atual && String(atual.id) === String(linha.id)
      ? null
      : { id: linha.id, x, y });
  };

  const adicionarLinha = async () => {
    try {
      const ultima = linhas[linhas.length - 1];
      const criada = await api(`/agenda-configuracoes/${agenda.id}/horarios`, {
        method: 'POST',
        body: JSON.stringify({
          horario: proximoHorario(ultima && ultima.horario, agenda.intervalo, agenda.hora_inicio),
          ordem: linhas.reduce((maior, l) => Math.max(maior, Number(l.ordem) || 0), 0) + 1,
          paciente: '', contato: '', observacao: '', pagamento: '', checkin: '', pos_consulta: '',
          status: 'disponivel', dados_extras: {}
        })
      });
      setLinhas((atuais) => [...atuais, criada]);
    } catch (e) { setErro(e.message); }
  };

  const excluirLinha = async (linha) => {
    if (!window.confirm(`Excluir o horário ${linha.horario || ''}${linha.paciente ? ` de ${linha.paciente}` : ''}?`)) return;
    try {
      await api(`/agenda-horarios/${linha.id}`, { method: 'DELETE' });
      setLinhas((atuais) => atuais.filter((l) => l.id !== linha.id));
    } catch (e) { setErro(e.message); }
  };

  const adicionarColuna = async () => {
    const nome = window.prompt('Nome da nova coluna:');
    if (!nome || !nome.trim()) return;
    const nova = [...colunas, { id: `extra_${Date.now()}`, nome: nome.trim() }];
    try {
      await api(`/agenda-configuracoes/${agenda.id}`, { method: 'PUT', body: JSON.stringify({ colunas_extras: nova }) });
      setColunas(nova);
    } catch (e) { setErro(e.message); }
  };
  const excluirColuna = async () => {
    if (!colunas.length) { setErro('Não há colunas extras para excluir.'); return; }
    const lista = colunas.map((coluna, indice) => `${indice + 1}. ${coluna.nome}`).join('\n');
    const resposta = window.prompt(`Qual coluna deseja excluir? Digite o número:\n${lista}`);
    if (resposta === null) return;
    const indice = Number(resposta) - 1;
    if (!Number.isInteger(indice) || indice < 0 || indice >= colunas.length) {
      setErro('Número de coluna inválido.');
      return;
    }
    const coluna = colunas[indice];
    if (!window.confirm(`Excluir a coluna “${coluna.nome}”? Os valores já preenchidos nela deixarão de aparecer.`)) return;
    const novas = colunas.filter((_, i) => i !== indice);
    try {
      await api(`/agenda-configuracoes/${agenda.id}`, { method: 'PUT', body: JSON.stringify({ colunas_extras: novas }) });
      setColunas(novas);
    } catch (e) { setErro(e.message); }
  };

  const duplicar = async (e) => {
    e.preventDefault();
    if (!dataDuplicar) return;
    try {
      setDuplicando(true);
      await api(`/agenda-configuracoes/${agenda.id}/replicar`, {
        method: 'POST', body: JSON.stringify({ data_destino: dataDuplicar, incluir_pacientes: false })
      });
      onDuplicar(dataDuplicar);
    } catch (err) { setErro(err.message); }
    finally { setDuplicando(false); }
  };

  const alternarLinhaSelecionada = (id) => {
    setLinhasSelecionadas((atuais) => {
      const chave = String(id);
      return atuais.some((item) => String(item) === chave)
        ? atuais.filter((item) => String(item) !== chave)
        : [...atuais, id];
    });
  };

  const alternarTodasLinhas = () => {
    const visiveis = linhasFiltradas.map((linha) => linha.id);
    const todasSelecionadas = visiveis.length > 0
      && visiveis.every((id) => linhasSelecionadas.some((item) => String(item) === String(id)));

    setLinhasSelecionadas((atuais) => {
      if (todasSelecionadas) {
        return atuais.filter((id) => !visiveis.some((visivel) => String(visivel) === String(id)));
      }

      return Array.from(new Set([...atuais, ...visiveis]));
    });
  };

  const copiarLinha = () => {
    const selecionadas = linhas.filter((linha) =>
      linhasSelecionadas.some((id) => String(id) === String(linha.id))
    );

    if (!selecionadas.length) {
      setErro('Selecione uma ou mais linhas antes de copiar.');
      return;
    }

    const campos = [
      'paciente',
      'contato',
      'observacao',
      'pagamento',
      'checkin',
      'pos_consulta',
      'status',
      'cor'
    ];

    const copiados = selecionadas.map((linha) => ({
      ...Object.fromEntries(
        campos.map((campo) => [campo, linha[campo] ?? null])
      ),
      dados_extras: OBJ(linha.dados_extras)
    }));

    setClipboard(copiados);
    setLinhasSelecionadas([]);
    setErro('');
  };

  const colarLinha = async () => {
    const destinos = linhas.filter((linha) =>
      linhasSelecionadas.some((id) => String(id) === String(linha.id))
    );

    if (!clipboard?.length) {
      setErro('Copie uma ou mais linhas antes de colar.');
      return;
    }

    if (!destinos.length) {
      setErro('Selecione um ou mais horários de destino.');
      return;
    }

    if (!window.confirm(`Colar dados em ${destinos.length} horário(s)?`)) return;

    try {
      const atualizacoes = destinos.map((destino, indice) => {
        const origem = clipboard.length === 1
          ? clipboard[0]
          : clipboard[indice % clipboard.length];

        return atualizarLinha(destino, origem);
      });

      await Promise.all(atualizacoes);
      setLinhasSelecionadas([]);
      setErro('');
    } catch (e) {
      setErro(e.message || 'Não foi possível colar as linhas.');
    }
  };

  const [duplicandoLinha, setDuplicandoLinha] = useState(false);
    const duplicarLinhas = async () => {
      const ids = linhasSelecionadas.map(String);
      const origens = linhas.filter((l) => ids.includes(String(l.id)));
      if (!origens.length) { setErro('Selecione uma linha para duplicar.'); return; }
      if (duplicandoLinha) return;
      setDuplicandoLinha(true);
      try {
        const criadas = new Map();
        for (const o of origens) {
          const criada = await api(`/agenda-configuracoes/${agenda.id}/horarios`, {
            method: 'POST',
            body: JSON.stringify({
              horario: o.horario, ordem: Number(o.ordem) || 0,
              paciente: o.paciente ?? '', contato: o.contato ?? '', observacao: o.observacao ?? '',
              pagamento: o.pagamento ?? '', checkin: o.checkin ?? '', pos_consulta: o.pos_consulta ?? '',
              status: o.status || 'disponivel', cor: null
            })
          });
          let final = criada;
          const extras = OBJ(o.dados_extras);
          if (Object.keys(extras).length) {
            const salva = await api(`/agenda-horarios/${criada.id}`, { method: 'PUT', body: JSON.stringify({ dados_extras: extras }) });
            final = { ...criada, ...salva };
          }
          criadas.set(String(o.id), final);
        }
        const nova = [];
        linhas.forEach((l) => { nova.push(l); const c = criadas.get(String(l.id)); if (c) nova.push(c); });
        const numerada = nova.map((l, i) => ({ ...l, ordem: i + 1 }));
        const alteradas = numerada.filter((l) => { const antes = linhas.find((x) => x.id === l.id); return !antes || Number(antes.ordem) !== l.ordem; });
        setLinhas(numerada);
        const idsNovos = Array.from(criadas.values()).map((c) => String(c.id));
        setLinhasRecemCriadas((atuais) => [...atuais, ...idsNovos]);
        setTimeout(() => setLinhasRecemCriadas((atuais) => atuais.filter((id) => !idsNovos.includes(id))), 4000);
        await Promise.all(alteradas.map((l) => api(`/agenda-horarios/${l.id}`, { method: 'PUT', body: JSON.stringify({ ordem: l.ordem }) })));
        setErro('');
      } catch (e) {
        setErro(e.message);
        await carregarLinhas();
      } finally { setDuplicandoLinha(false); }
    };

    const [transferirAberto, setTransferirAberto] = useState(false);
  const [dataTransferir, setDataTransferir] = useState('');
  const [transferindo, setTransferindo] = useState(false);
  const [excluindoLinhas, setExcluindoLinhas] = useState(false);

  const transferirAgenda = async () => {
    const nova = TEXTO(dataTransferir).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(nova)) { setErro('Informe uma data válida.'); return; }
    if (nova === TEXTO(agenda.data).slice(0, 10)) { setErro('Escolha uma data diferente da atual.'); return; }
    setTransferindo(true);
    try {
      await api(`/agenda-configuracoes/${agenda.id}`, { method: 'PUT', body: JSON.stringify({ data: nova }) });
      setTransferirAberto(false);
      setErro('');
      if (typeof onAtualizar === 'function') {
        await onAtualizar();
        if (typeof onEscolherDia === 'function') onEscolherDia(nova);
      } else {
        window.location.reload();
      }
    } catch (e) {
      setErro(e.message);
    } finally {
      setTransferindo(false);
    }
  };

  const excluirSelecionadas = async () => {
    const ids = linhasSelecionadas.map(String);
    const alvo = linhas.filter((l) => ids.includes(String(l.id)));
    if (!alvo.length) { setErro('Selecione uma ou mais linhas para excluir.'); return; }
    const comPaciente = alvo.filter((l) => TEXTO(l.paciente).trim()).length;
    const aviso = comPaciente
      ? `Excluir ${alvo.length} horário(s)? ${comPaciente} deles tem paciente preenchido. Esta ação não pode ser desfeita.`
      : `Excluir ${alvo.length} horário(s)? Esta ação não pode ser desfeita.`;
    if (!window.confirm(aviso)) return;
    if (excluindoLinhas) return;
    setExcluindoLinhas(true);
    try {
      const resultados = await Promise.allSettled(alvo.map((l) => api(`/agenda-horarios/${l.id}`, { method: 'DELETE' })));
      const removidos = new Set();
      resultados.forEach((r, i) => { if (r.status === 'fulfilled') removidos.add(String(alvo[i].id)); });
      setLinhas((atuais) => atuais.filter((l) => !removidos.has(String(l.id))));
      setLinhasSelecionadas([]);
      const falhas = alvo.length - removidos.size;
      setErro(falhas ? `${falhas} horário(s) não puderam ser excluídos.` : '');
    } finally {
      setExcluindoLinhas(false);
    }
  };

  const ordenarHorarios = async () => {
    const anteriores = linhas;
    const ordenadas = [...linhas].sort((a, b) => {
      const diferenca = minutosHorario(a.horario) - minutosHorario(b.horario);
      return Number.isFinite(diferenca) && diferenca !== 0
        ? diferenca
        : (Number(a.ordem) || 0) - (Number(b.ordem) || 0);
    });
    setLinhas(ordenadas);
    try {
      await Promise.all(ordenadas.map((linha, i) => api(`/agenda-horarios/${linha.id}`, {
        method: 'PUT', body: JSON.stringify({ ordem: i + 1 })
      })));
    } catch (e) {
      setLinhas(anteriores);
      setErro(e.message);
    }
  };

  const profissionalNome = nomeProfEditado || profissional || agenda.medico_nome || agenda.nome || 'Profissional';
  const dataBr = TEXTO(agenda.data).slice(0, 10).split('-').reverse().join('/');
  const extras = colunas;
  const colunasTabela = [...CAMPOS, ...extras.map((c) => [`extra:${c.id}`, c.nome || 'Coluna'])];
  const colunaAntesDoStatus = setor === 'odontologia' ? 'observacao' : '';
  const idxAntesStatus = colunasTabela.findIndex(([c]) => c === colunaAntesDoStatus);
  const colunasAntesStatus = idxAntesStatus >= 0 ? colunasTabela.slice(0, idxAntesStatus + 1) : colunasTabela;
  const colunasDepoisStatus = idxAntesStatus >= 0 ? colunasTabela.slice(idxAntesStatus + 1) : [];
  const chavesCols = ['#', ...colunasAntesStatus.map(([c]) => c), 'status', ...colunasDepoisStatus.map(([c]) => c), 'acoes'];
  const nomesCols = Object.fromEntries(colunasTabela.map(([c, n]) => [c, rotuloCampo(c, n, setor)]));
  const LARGURAS_PADRAO = { '#': 36, horario: 105, paciente: 180, contato: 145, observacao: 145, pagamento: 130, checkin: 140, pos_consulta: 140, status: 150, acoes: 74 };
  const larguraDe = (k) => larg[k] || LARGURAS_PADRAO[k] || 150;
  const somaLarguras = chavesCols.reduce((total, chave) => total + larguraDe(chave), 0);
  const salvarLarg = () => { try { localStorage.setItem('agset-larg-agenda', JSON.stringify(largRef.current)); } catch (_) { /* sem armazenamento */ } };
  const iniciarResize = (e, chave) => {
    e.preventDefault();
    e.stopPropagation();
    const x0 = e.clientX;
    const atual = e.currentTarget.parentElement.offsetWidth;
    document.body.classList.add('agset-redimensionando');
    const mover = (ev) => {
      largRef.current = { ...largRef.current, [chave]: Math.max(70, Math.round(atual + ev.clientX - x0)) };
      setLarg(largRef.current);
    };
    const soltar = () => {
      document.removeEventListener('pointermove', mover);
      document.removeEventListener('pointerup', soltar);
      document.body.classList.remove('agset-redimensionando');
      salvarLarg();
    };
    document.addEventListener('pointermove', mover);
    document.addEventListener('pointerup', soltar);
  };
  const restaurarLarg = (chave) => {
    const novas = { ...largRef.current };
    delete novas[chave];
    largRef.current = novas;
    setLarg(novas);
    salvarLarg();
  };
  const valoresFiltro = (campo) => Array.from(new Set(linhas.map((linha) => TEXTO(linha[campo]).trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  const temFiltrosAtivos = Boolean(buscaLinha.trim() || Object.values(filtrosColuna).some(Boolean));
  const limparFiltros = () => { setBuscaLinha(''); setFiltrosColuna({ checkin: '', pos_consulta: '', status: '' }); };
  const linhasFiltradas = useMemo(() => {
    const termo = buscaLinha.trim().toLocaleLowerCase('pt-BR');
    return linhas.filter((linha) => {
      if (termo && !`${TEXTO(linha.paciente)} ${TEXTO(linha.contato)}`.toLocaleLowerCase('pt-BR').includes(termo)) return false;
      for (const campo of ['checkin', 'pos_consulta', 'status']) {
        const filtro = filtrosColuna[campo];
        if (!filtro) continue;
        const valor = TEXTO(linha[campo]).trim();
        if (filtro === '__VAZIO__' && valor) return false;
        if (filtro.startsWith('__VALOR__:') && valor !== filtro.slice(10)) return false;
      }
      return true;
    });
  }, [linhas, buscaLinha, filtrosColuna]);

  const exportarAgendaAberta = () => {
    try {
      const rotuloStatus = (valor) => (STATUS.find(([v]) => v === valor) || [])[1] || valor || '';
      const dataIso = TEXTO(agenda.data).slice(0, 10);
      const dataTxt = dataIso.split('-').reverse().join('/');
      const diaSem = dataIso ? new Date(`${dataIso}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long' }) : '';
      const profissional = agenda.medico_nome || agenda.profissional_nome || agenda.profissional || '';
      const cabecalho = [...CAMPOS.map(([campo, rotulo]) => rotuloCampo(campo, rotulo, setor)), 'Status', ...colunas.map((c) => c.nome || 'Coluna')];
      const corpo = linhas.map((linha) => [
        ...CAMPOS.map(([campo]) => TEXTO(linha[campo] ?? '')),
        rotuloStatus(linha.status),
        ...colunas.map((c) => TEXTO(OBJ(linha.dados_extras)[c.id] ?? '')),
      ]);
      const topo = [
        [`Agenda de atendimentos - ${dataTxt}`],
        ['Especialidade', agenda.especialidade || agenda.nome || ''],
        ['Profissional', profissional],
        ['Data', `${dataTxt}${diaSem ? ` (${diaSem})` : ''}`],
        ['Funcionamento', `${agenda.hora_inicio || ''} - ${agenda.hora_fim || ''} · ${agenda.intervalo ?? ''} min`],
        ['Pacientes marcados', `${linhas.filter((l) => TEXTO(l.paciente).trim()).length} de ${linhas.length} horários`],
        [],
      ];
      const aba = XLSX.utils.aoa_to_sheet([...topo, cabecalho, ...corpo]);
      aba['!cols'] = cabecalho.map((titulo, i) => ({
        wch: Math.min(40, Math.max(String(titulo).length + 2, 14, ...corpo.map((l) => String(l[i] ?? '').length + 2))),
      }));
      const livro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(livro, aba, 'Agenda');
      const nomeArq = `Agenda_${TEXTO(agenda.especialidade || agenda.nome || 'agenda').replace(/[^a-zA-Z0-9À-ú]+/g, '_')}_${dataTxt.replace(/\//g, '-')}.xlsx`;
      XLSX.writeFile(livro, nomeArq);
    } catch (e) { setErro(`Não foi possível exportar: ${e.message}`); }
  };

  return (
    <>
      <div className="agset-topbar">
        {onVoltar && <button type="button" className="agset-secondary agset-topbar-back" title="Voltar às agendas" onClick={onVoltar}><ArrowLeft size={14} /> Voltar</button>}
        <button type="button" className="agset-secondary" onClick={adicionarColuna}><Plus size={15} /> Coluna</button>
        <button type="button" className="agset-secondary agset-delete-column" onClick={excluirColuna} disabled={!colunas.length}><Trash2 size={14} /> Excluir coluna</button><button type="button" className="agset-secondary agset-transfer-btn" onClick={() => { setDataTransferir(SOMA_DIAS(TEXTO(agenda.data).slice(0, 10), 7)); setTransferirAberto(true); }}>Transferir agenda</button><button type="button" className="agset-secondary agset-export-btn" onClick={exportarAgendaAberta} disabled={carregando}><FileSpreadsheet size={14} /> Exportar Excel</button>{transferirAberto && <div className="agset-transfer-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !transferindo) setTransferirAberto(false); }}><div className="agset-transfer-modal"><h3>Transferir agenda</h3><p>Todos os horários e pacientes da agenda de {dataBr} serão movidos para a nova data.</p><label>Nova data<DateInputBR value={dataTransferir} onChange={setDataTransferir} /></label><div className="agset-transfer-actions"><button type="button" className="agset-secondary" onClick={() => setTransferirAberto(false)} disabled={transferindo}>Cancelar</button><button type="button" className="agset-primary" onClick={transferirAgenda} disabled={transferindo}>{transferindo ? 'Transferindo…' : 'Transferir'}</button></div></div></div>}
      </div>
      <section className="agset-card">
      <div className="agset-card-head">
        <div className="agset-card-title">
          <div className="agset-title-row"><span className="agset-kicker">Agenda de atendimentos · {dataBr}</span></div>
          <h2>{agenda.especialidade || agenda.nome || 'Agenda'}</h2>
          <p>{profissionalNome} · {agenda.hora_inicio || '—'}–{agenda.hora_fim || '—'} · {agenda.intervalo || 20} min</p>
        </div>
        <div className="agset-head-actions agset-hm-host">
          {!editandoAg && <div className="agset-hm-wrap">
            <button type="button" className="agset-hm-btn" title="Ações da agenda" aria-label="Ações da agenda" aria-haspopup="menu" aria-expanded={menuAgAberto} onClick={() => setMenuAgAberto((v) => !v)}><MoreVertical size={17} /></button>
            {menuAgAberto && <div className="agset-hm-menu" role="menu">
              <button type="button" role="menuitem" onClick={abrirEditarAg}><Edit3 size={14} /> Editar</button>
              <button type="button" role="menuitem" onClick={arquivarDiaAg}><Archive size={14} /> Arquivar</button>
              <button type="button" role="menuitem" className="danger" onClick={excluirAg}><Trash2 size={14} /> Excluir</button>
            </div>}
          </div>}
          {mostrarControles && <>
            <label className="agset-inline-specialty"><span>Especialidade</span><select value={grupoSelecionado.chave} onChange={(e) => onTrocarGrupo(e.target.value)}>{[grupoSelecionado, ...gruposAgendas.filter((grupo) => grupo.chave !== grupoSelecionado.chave)].map((grupo) => <option key={grupo.chave} value={grupo.chave}>{grupo.especialidade} · {grupo.medicoNome}</option>)}</select></label>
            <div className="agset-inline-calendar"><span>Calendário</span><DayPickerBR value={TEXTO(agenda.data).slice(0, 10)} onChange={onEscolherDia} diasComAgenda={blocosDatas.map((b) => TEXTO(b.data).slice(0, 10))} /></div>
          </>}
          
          
          
          
        </div>
      </div>
      <div className="agset-meta-wrap"><div className="agset-meta"><span><b>Profissional</b>{editandoAg ? <input className="agset-hm-input" aria-label="Nome do profissional" value={formAg.nome} onChange={(e) => setFormAg((f) => ({ ...f, nome: e.target.value }))} /> : profissionalNome}</span><span><b>Data da agenda</b>{dataBr}</span><span className="agset-values-meta">
  <b>Valor</b>
  <span className="agset-value-line">
    <small>Associado</small>
    <strong>{editandoAg ? <input className="agset-hm-input agset-hm-input-val" inputMode="decimal" aria-label="Valor associado" value={formAg.associado} onChange={(e) => setFormAg((f) => ({ ...f, associado: e.target.value }))} /> : (agenda.valor_cartao || '—')}</strong>
  </span>
  <span className="agset-value-line">
    <small>Particular</small>
    <strong>{editandoAg ? <input className="agset-hm-input agset-hm-input-val" inputMode="decimal" aria-label="Valor particular" value={formAg.particular} onChange={(e) => setFormAg((f) => ({ ...f, particular: e.target.value }))} /> : (agenda.valor_particular || '—')}</strong>
  </span>
</span><span className="agset-info-extra"><b>Informações adicionais</b>{editandoObs ? <><textarea className="agset-info-edit" rows={4} autoFocus value={rascunhoObs} onChange={(e) => setRascunhoObs(e.target.value)} placeholder="Uma informação por linha" />{editandoAg ? <div className="agset-info-actions"><button type="button" onClick={salvarEditarAg} disabled={salvandoAg}>{salvandoAg ? 'Salvando…' : 'Salvar alterações'}</button><button type="button" onClick={cancelarEditarAg} disabled={salvandoAg}>Cancelar</button></div> : <div className="agset-info-actions"><button type="button" onClick={salvarObs}>Salvar</button><button type="button" onClick={() => setEditandoObs(false)}>Cancelar</button></div>}</> : (obsInfo ? obsInfo.split('\n').map((linha, i) => <em key={i}>{linha}</em>) : '—')}</span></div><form className="agset-duplicate" onSubmit={duplicar}><Copy size={14} /><label>Duplicar para <DateInputBR value={dataDuplicar} onChange={setDataDuplicar} /></label><button type="submit" disabled={duplicando}>{duplicando ? 'Duplicando…' : 'Duplicar Agenda'}</button></form></div><div className="agset-tools"><span className="agset-total-agend" title="Pacientes agendados nesta agenda">Total de agendamentos: <strong>{linhas.filter((l) => String(l.paciente == null ? '' : l.paciente).trim()).length}</strong></span><span className="agset-total-agend agset-total-checkin" title="Linhas com Check-in preenchido como OK">Check-in: <strong>{linhas.filter((l) => String(l.checkin == null ? '' : l.checkin).trim().toLowerCase() === 'ok').length}</strong></span><button type="button" className="agset-secondary agset-new-agenda-inline" onClick={onNovaAgenda}><Plus size={14} /> Nova agenda</button>{temFiltrosAtivos && <button type="button" className="agset-secondary" onClick={limparFiltros}>Limpar filtros</button>}{linhasSelecionadas.length > 0 && <button type="button" className="agset-secondary agset-delete-selected" onClick={excluirSelecionadas} disabled={excluindoLinhas}><Trash2 size={14} /> {excluindoLinhas ? 'Excluindo…' : `Excluir selecionadas (${linhasSelecionadas.length})`}</button>}{linhasSelecionadas.length > 0 ? <button type="button" className="agset-secondary agset-add-row-top" onClick={duplicarLinhas} disabled={duplicandoLinha}><Copy size={14} /> Duplicar linha</button> : <button type="button" className="agset-secondary agset-add-row-top" onClick={adicionarLinha}><Plus size={15} /> Adicionar linha</button>}</div>
      {erro && <div className="agset-error" role="alert">{erro}<button type="button" onClick={() => setErro('')} aria-label="Fechar erro"><X size={14} /></button></div>}
      <div className="agset-table-wrap">
        <table className="agset-table agset-resizable" style={{ '--agset-tw': `${somaLarguras}px` }}><colgroup>{chavesCols.map((k) => <col key={k} style={{ width: larguraDe(k) }} />)}</colgroup>
          <thead><tr ref={headRef}>{chavesCols.map((k) => {
            const campoFiltro = k === 'checkin' || k === 'pos_consulta' || k === 'status' ? k : null;
            const opcoes = campoFiltro ? valoresFiltro(campoFiltro) : [];
            const filtroAtual = campoFiltro ? filtrosColuna[campoFiltro] : '';
            const rotulo = k === '#' ? '#' : k === 'status' ? 'Status' : k === 'acoes' ? 'Ações' : nomesCols[k];
            return <th key={k}><span className="agset-th-content">{k === '#' ? <input
              type="checkbox"
              className="agset-select-all"
              checked={linhasFiltradas.length > 0 && linhasFiltradas.every((linha) => linhasSelecionadas.some((id) => String(id) === String(linha.id)))}
              onChange={alternarTodasLinhas}
              aria-label="Selecionar todas as linhas"
            /> : <span>{rotulo}</span>}{campoFiltro && <span className={`agset-th-filter ${filtroAtual ? 'active' : ''}`} title={`Filtrar ${rotulo}`}><Filter size={13} /><select aria-label={`Filtrar ${rotulo}`} value={filtroAtual} onChange={(evento) => setFiltrosColuna((atuais) => ({ ...atuais, [campoFiltro]: evento.target.value }))}><option value="">Todos</option><option value="__VAZIO__">Vazios</option>{opcoes.map((valor) => <option key={valor} value={`__VALOR__:${valor}`}>{campoFiltro === 'status' ? (STATUS.find(([v]) => v === valor)?.[1] || valor) : valor}</option>)}</select></span>}</span><span className="agset-col-resize" title="Arraste para ajustar a largura (duplo clique restaura)" onPointerDown={(e) => iniciarResize(e, k)} onDoubleClick={() => restaurarLarg(k)} /></th>;
          })}</tr></thead>
          <tbody>
            {carregando ? <tr><td colSpan={colunasTabela.length + 3} className="agset-empty">Carregando horários…</td></tr> : linhasFiltradas.length === 0 ? <tr><td colSpan={colunasTabela.length + 3} className="agset-empty">{linhas.length ? (temFiltrosAtivos ? 'Nenhuma linha corresponde aos filtros aplicados.' : 'Nenhum paciente ou telefone encontrado.') : 'Nenhum horário cadastrado.'}</td></tr> : linhasFiltradas.map((linha, index) => (
              <tr key={linha.id} className={`${linhasRecemCriadas.includes(String(linha.id)) ? 'agset-row-nova ' : ''}${String(linha.id) === String(linhaSelecionadaId) ? 'selected' : ''} ${linhasSelecionadas.some((id) => String(id) === String(linha.id)) ? 'agset-row-selected' : ''} ${(CORES_STATUS_LINHA[linha.status] || linha.cor) ? 'colored' : ''}`} style={(CORES_STATUS_LINHA[linha.status] || linha.cor) ? { '--agset-row-color': CORES_STATUS_LINHA[linha.status] || linha.cor } : undefined}>
                <td className="agset-row-number">
                  <label className="agset-row-select">
                    <input
                      type="checkbox"
                      checked={linhasSelecionadas.some((id) => String(id) === String(linha.id))}
                      onChange={() => alternarLinhaSelecionada(linha.id)}
                      aria-label={`Selecionar horário ${linha.horario || index + 1}`}
                    />
                    <span>{index + 1}</span>
                  </label>
                </td>
                {colunasAntesStatus.map(([campo, _nome, tipo]) => {
                  const extra = campo.startsWith('extra:');
                  const idExtra = extra ? campo.slice(6) : '';
                  const valor = extra ? OBJ(linha.dados_extras)[idExtra] : linha[campo];
                  return <td key={campo}><input className={tipo === 'time' ? 'agset-time' : ''} type="text" {...(tipo === 'time' ? { inputMode: 'numeric', maxLength: 5, placeholder: 'HH:MM', autoComplete: 'off' } : {})} value={TEXTO(valor)} onFocus={() => setLinhaSelecionadaId(linha.id)} onChange={(e) => {
                    const novo = tipo === 'time' ? MASCARA_HORA(e.target.value) : campo === 'contato' ? MASCARA_TEL(e.target.value) : e.target.value;
                    setLinhas((atuais) => atuais.map((l) => {
                      if (l.id !== linha.id) return l;
                      if (!extra) return { ...l, [campo]: novo };
                      return { ...l, dados_extras: { ...OBJ(l.dados_extras), [idExtra]: novo } };
                    }));
                  }} onBlur={(e) => {
                    const novo = tipo === 'time' ? NORMALIZA_HORA(e.target.value, linha[campo]) : campo === 'contato' ? FINAL_TEL(e.target.value) : e.target.value;
                    if (!extra) atualizarLinha(linha, { [campo]: novo });
                    else { const extrasAtuais = { ...OBJ(linha.dados_extras), [idExtra]: novo }; atualizarLinha(linha, { dados_extras: extrasAtuais }); }
                  }} /></td>;
                })}
                <td><div className="agset-status-pill" data-status={linha.status || 'disponivel'}><span className="agset-status-dot" /><StatusEditavel valor={linha.status} idLista={`agset-st-${linha.id}`} onFocus={() => setLinhaSelecionadaId(linha.id)} onSalvar={(novo) => atualizarLinha(linha, { status: novo })} opcoesExtras={Array.from(new Set(linhas.map((l) => l.status).filter((s) => s && !STATUS.some(([chave]) => chave === s))))} /></div></td>
                {colunasDepoisStatus.map(([campo, _nome, tipo]) => {
                  const extra = campo.startsWith('extra:');
                  const idExtra = extra ? campo.slice(6) : '';
                  const valor = extra ? OBJ(linha.dados_extras)[idExtra] : linha[campo];
                  return <td key={campo}><input className={tipo === 'time' ? 'agset-time' : ''} type="text" {...(tipo === 'time' ? { inputMode: 'numeric', maxLength: 5, placeholder: 'HH:MM', autoComplete: 'off' } : {})} value={TEXTO(valor)} onFocus={() => setLinhaSelecionadaId(linha.id)} onChange={(e) => {
                    const novo = tipo === 'time' ? MASCARA_HORA(e.target.value) : campo === 'contato' ? MASCARA_TEL(e.target.value) : e.target.value;
                    setLinhas((atuais) => atuais.map((l) => {
                      if (l.id !== linha.id) return l;
                      if (!extra) return { ...l, [campo]: novo };
                      return { ...l, dados_extras: { ...OBJ(l.dados_extras), [idExtra]: novo } };
                    }));
                  }} onBlur={(e) => {
                    const novo = tipo === 'time' ? NORMALIZA_HORA(e.target.value, linha[campo]) : campo === 'contato' ? FINAL_TEL(e.target.value) : e.target.value;
                    if (!extra) atualizarLinha(linha, { [campo]: novo });
                    else { const extrasAtuais = { ...OBJ(linha.dados_extras), [idExtra]: novo }; atualizarLinha(linha, { dados_extras: extrasAtuais }); }
                  }} /></td>;
                })}
                <td className="agset-row-actions"><div className="agset-action-buttons"><div className="agset-color-menu">
                    <button
                      type="button"
                      className="agset-color-trigger"
                      data-agset-color-trigger="true"
                      title="Alterar cor da linha"
                      aria-label="Alterar cor da linha"
                      aria-expanded={!!corAberta && String(corAberta.id) === String(linha.id)}
                      onClick={(evento) => abrirPaletaCor(evento, linha)}
                    >
                      <Palette
                        size={15}
                        color={/^#[0-9a-f]{6}$/i.test(TEXTO(linha.cor)) ? linha.cor : '#64748b'}
                      />
                    </button>
                  </div><button type="button" className="agset-icon-button danger" title="Excluir linha" onClick={() => excluirLinha(linha)}><Trash2 size={15} /></button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="agset-table-foot"><span>{temFiltrosAtivos ? `Exibindo ${linhasFiltradas.length} de ${linhas.length}` : `${linhas.length} horários`} · novas linhas começam vazias</span></div>
    </section>
    {corLinhaModal && createPortal((() => {
      const v = TEXTO(corLinhaModal.valor);
      const naPaleta = CORES_CARD_AGENDA.some((c) => c.toLowerCase() === v.toLowerCase());
      const linhaModal = linhas.find((item) => String(item.id) === String(corLinhaModal.id));
      const rotulo = linhaModal ? [linhaModal.horario, linhaModal.paciente].filter(Boolean).join(' · ') : '';
      return (
        <div className="agset-rc-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !salvandoCorLinha) setCorLinhaModal(null); }}>
          <div className="agset-rc-modal" role="dialog" aria-label="Cor da linha">
            <span className="agset-rc-eyebrow">Organização</span>
            <h2>Cor da linha</h2>
            <p>{rotulo || 'Escolha a cor desta linha'}</p>
            <div className="agset-rc-grid" role="radiogroup" aria-label="Cor da linha">
              <button type="button" role="radio" aria-checked={v === ''} aria-label="Sem cor" title="Sem cor" className={`agset-rc-dot agset-rc-none${v === '' ? ' is-selected' : ''}`} onClick={() => setCorLinhaModal((m) => ({ ...m, valor: '' }))} />
              {CORES_CARD_AGENDA.map((cor) => <button key={cor} type="button" role="radio" aria-checked={v.toLowerCase() === cor.toLowerCase()} aria-label={`Cor ${cor}`} className={`agset-rc-dot${v.toLowerCase() === cor.toLowerCase() ? ' is-selected' : ''}`} style={{ background: cor }} onClick={() => setCorLinhaModal((m) => ({ ...m, valor: cor }))}>{v.toLowerCase() === cor.toLowerCase() ? '✓' : ''}</button>)}
              <label className={`agset-rc-custom${v && !naPaleta ? ' is-selected' : ''}`} title="Escolher outra cor" style={v && !naPaleta ? { background: v } : undefined}>
                <input type="color" value={/^#[0-9a-f]{6}$/i.test(v) ? v : '#4d56d8'} onChange={(e) => setCorLinhaModal((m) => ({ ...m, valor: e.target.value.toUpperCase() }))} aria-label="Escolher outra cor" /><span>+</span>
              </label>
            </div>
            <div className="agset-rc-footer">
              <button type="button" className="agset-rc-cancel" onClick={() => setCorLinhaModal(null)} disabled={salvandoCorLinha}>Cancelar</button>
              <button type="button" className="agset-rc-save" onClick={salvarCorLinha} disabled={salvandoCorLinha}>{salvandoCorLinha ? 'Salvando…' : 'Salvar cor'}</button>
            </div>
          </div>
        </div>
      );
    })(), document.body)}
    </>
  );
}

function DiaVazio({ grupoSelecionado, gruposAgendas, onTrocarGrupo, dia, onEscolherDia, diasComAgenda, onVoltar }) {
  return (
    <section className="agset-card">
      <div className="agset-card-head">
        <div className="agset-card-title">
          <div className="agset-title-row">{onVoltar && <button type="button" className="agset-back-icon agset-back-inline" title="Voltar às agendas" aria-label="Voltar às agendas" onClick={onVoltar}><ArrowLeft size={15} /><span>Voltar</span></button>}<span className="agset-kicker">Agenda de atendimentos · {isoParaBR(dia)}</span></div>
          <h2>{grupoSelecionado.especialidade || 'Agenda'}</h2>
          <p>{grupoSelecionado.medicoNome}</p>
        </div>
        <div className="agset-head-actions">
          <label className="agset-inline-specialty"><span>Especialidade</span><select value={grupoSelecionado.chave} onChange={(e) => onTrocarGrupo(e.target.value)}>{[grupoSelecionado, ...gruposAgendas.filter((grupo) => grupo.chave !== grupoSelecionado.chave)].map((grupo) => <option key={grupo.chave} value={grupo.chave}>{grupo.especialidade} · {grupo.medicoNome}</option>)}</select></label>
          <div className="agset-inline-calendar"><span>Calendário</span><DayPickerBR value={dia} onChange={onEscolherDia} diasComAgenda={diasComAgenda} /></div>
        </div>
      </div>
      <div className="agset-day-empty">Nenhum agendamento nesse dia.</div>
    </section>
  );
}

// Seletor de horário sempre em 24h (o input type="time" segue o idioma do sistema e pode mostrar AM/PM).
function HoraInput24({ value, onChange }) {
  const texto = String(value || '');
  const valido = /^\d{1,2}:\d{2}/.test(texto);
  const hora = valido ? texto.split(':')[0].padStart(2, '0') : '';
  const minuto = valido ? texto.split(':')[1].slice(0, 2) : '';
  const horas = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const base = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));
  const minutos = minuto && !base.includes(minuto) ? [...base, minuto].sort() : base;
  const mudar = (novaHora, novoMinuto) => onChange(`${novaHora || '00'}:${novoMinuto || '00'}`);
  return (
    <span className="hora24">
      <select aria-label="Hora" value={hora} onChange={(e) => mudar(e.target.value, minuto)}>
        {!hora && <option value="">--</option>}
        {horas.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="hora24-sep">:</span>
      <select aria-label="Minuto" value={minuto} onChange={(e) => mudar(hora, e.target.value)}>
        {!minuto && <option value="">--</option>}
        {minutos.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <span className="hora24-sufixo">h</span>
    </span>
  );
}

// Campo de data do modal "Baixar agendamentos do dia" (classes próprias, sem herdar estilos antigos).
function DataExportBR({ value, onChange }) {
  const paraBR = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso || '') ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');
  const [textoDia, setTextoDia] = useState(paraBR(value));
  const nativo = useRef(null);

  useEffect(() => { setTextoDia(paraBR(value)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const digitar = (bruto) => {
    const d = bruto.replace(/\D/g, '').slice(0, 8);
    const formatado = d.length > 4 ? `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}` : d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
    setTextoDia(formatado);
    if (d.length === 8) {
      const iso = `${d.slice(4)}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
      const data = new Date(`${iso}T12:00:00`);
      if (!Number.isNaN(data.getTime()) && data.getDate() === Number(d.slice(0, 2))) onChange(iso);
    }
  };

  const abrirCalendario = () => {
    try { nativo.current?.showPicker(); } catch (_) { nativo.current?.click(); }
  };

  return (
    <span className="agexp-data">
      <input type="text" inputMode="numeric" className="agexp-data-input" placeholder="dd/mm/aaaa" value={textoDia} onChange={(e) => digitar(e.target.value)} aria-label="Dia" />
      <button type="button" className="agexp-data-btn" title="Abrir calendário" aria-label="Abrir calendário" onClick={abrirCalendario}><CalendarDays size={17} /></button>
      <input ref={nativo} type="date" tabIndex={-1} aria-hidden="true" className="agexp-data-nativo" value={value || ''} onChange={(e) => onChange(e.target.value)} />
    </span>
  );
}

export default function AgendamentosSetorial({ setor }) {
  const titulo = TITULOS[setor] || 'Setor não disponível';
  const navegar = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const chaveSelecionadaURL = searchParams.get('agenda');
  const agendaIdURL = searchParams.get('agendaId');
  const abrirNovaURL = searchParams.get('nova') === '1';
  const diaInicialURL = String(searchParams.get('dia') || '').slice(0, 10);
  const [meses, setMeses] = useState(() => iniciarMeses(setor));
  const [abasAbertas, setAbasAbertas] = useState(false);
  const [mes, setMes] = useState(() => {
    const lista = iniciarMeses(setor);
    const atual = MES_ATUAL();
    const mesURL = /^\d{4}-\d{2}-\d{2}$/.test(diaInicialURL)
      ? diaInicialURL.slice(0, 7)
      : '';

    return mesURL && lista.includes(mesURL)
      ? mesURL
      : lista.includes(atual)
        ? atual
        : lista[0];
  });
  const [medicos, setMedicos] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [consultorios, setConsultorios] = useState([]);
  const [agendas, setAgendas] = useState([]);
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [filtroEspecialidade, setFiltroEspecialidade] = useState('');
  const [grupoSelecionado, setGrupoSelecionado] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [modalAgenda, setModalAgenda] = useState(false);
    const [agendaEditando, setAgendaEditando] = useState(null);
    const [mostrarArquivadas, setMostrarArquivadas] = useState(false);
    const [menuAgendaAberto, setMenuAgendaAberto] = useState(null);
  const [modalEspecialidade, setModalEspecialidade] = useState(false);
  const [nomesAbas, setNomesAbas] = useState(() => { try { const v = JSON.parse(localStorage.getItem(`agendamentos-nomes-abas-${setor}`)); return v && typeof v === 'object' ? v : {}; } catch (_) { return {}; } });
  const [abaRenomeando, setAbaRenomeando] = useState(null);
  const [nomeAbaEdicao, setNomeAbaEdicao] = useState('');
  const rotuloAba = (m) => nomesAbas[m] || ROTULO_ABA_MES(m);
  const [novaEspecialidade, setNovaEspecialidade] = useState('');
  const [form, setForm] = useState({ tipo: 'Agenda médica', especialidade: '', profissional: '', medicoId: '', data: '', inicio: '09:00', fim: '14:00', intervalo: 20, valorAssociado: '', valorParticular: '', cor: '#11c9d3', consultorio_id: '', observacoes: '' });
  const [salvando, setSalvando] = useState(false);
  const [blocoId, setBlocoId] = useState(null);
  const [corGrupoAberto, setCorGrupoAberto] = useState(null);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const { user: usuarioLogado } = useAuth();
  // Ícone antigo dentro de Gestão de Agendas:
  // somente Admin, Administrador, Gestor e Marketing.
  const podeBaixarDia = (usuarioLogado?.roles || []).some((papel) =>
    ['admin', 'administrador', 'gestor'].includes(
      String(papel).trim().toLowerCase()
    )
  );

  // Novo botão separado na barra superior.
  // O usuário já precisa ter acesso à Central de Agendas para chegar nesta tela.
  const podeRelatorio = true;
  const podeExportar = (usuarioLogado?.roles || []).some((papel) =>
    ['admin', 'administrador', 'gestor', 'marketing', 'financeiro'].includes(String(papel).trim().toLowerCase())
  );
  const [corGrupoValor, setCorGrupoValor] = useState('#4D56D8');
  const [salvandoCorGrupo, setSalvandoCorGrupo] = useState(false);
  const [diaSel, setDiaSel] = useState(() => (
    /^\d{4}-\d{2}-\d{2}$/.test(diaInicialURL)
      ? diaInicialURL
      : HOJE_ISO()
  ));
  const [diaEscolhidoManualmente, setDiaEscolhidoManualmente] = useState(
    () => /^\d{4}-\d{2}-\d{2}$/.test(diaInicialURL)
  );

  useEffect(() => { localStorage.setItem(`agendamentos-meses-${setor}`, JSON.stringify(meses)); }, [meses, setor]);
  // se um mês oculto voltar a ser usado (adicionado ou destino de duplicação), a aba volta a valer
  useEffect(() => {
    try {
      const o = MESES_OCULTOS(setor);
      if (o.includes(mes)) localStorage.setItem(`agendamentos-meses-ocultos-${setor}`, JSON.stringify(o.filter((m) => m !== mes)));
    } catch (_) { /* sem storage */ }
  }, [mes, setor]);

  const carregarCadastros = useCallback(async () => {
    try {
      const [listaMedicos, listaEspecialidades, listaConsultorios] = await Promise.all([
        api(`/medicos?setor=${encodeURIComponent(setor)}`),
        api(`/especialidades?setor=${encodeURIComponent(setor)}`),
        api(`/consultorios?setor=${encodeURIComponent(setor)}`)
      ]);
      setMedicos(Array.isArray(listaMedicos) ? listaMedicos : []);
      setEspecialidades(Array.isArray(listaEspecialidades) ? listaEspecialidades : []);
      setConsultorios(Array.isArray(listaConsultorios) ? listaConsultorios : []);
    } catch (e) { setErro(e.message); }
  }, [setor]);

  // Modal antigo: exportação restrita de PDF/Excel.
  const [modalExport, setModalExport] = useState(false);

  // Modal novo: relatório separado para os usuários da Central.
  const [modalRelatorio, setModalRelatorio] = useState(false);

  const [diaExport, setDiaExport] = useState('');
  const [exportando, setExportando] = useState(false);
  const [erroExport, setErroExport] = useState('');
  // Função antiga: permanece no ícone de Gestão de Agendas.
  const abrirExport = () => {
    if (!podeBaixarDia) return;
    setDiaExport(new Date().toLocaleDateString('sv-SE'));
    setErroExport('');
    setModalExport(true);
  };

  // Novo relatório: permanece separado na barra superior.
  const abrirRelatorio = () => {
    if (!podeRelatorio) return;
    setDiaExport(new Date().toLocaleDateString('sv-SE'));
    setErroExport('');
    setModalRelatorio(true);
  };
  const dataBrExp = (iso) => TEXTO(iso).slice(0, 10).split('-').reverse().join('/');
  const montarRelatorio = async () => {
    if (!diaExport) throw new Error('Escolha o dia.');
    const linhas = await api(`/agenda-horarios/agendados?dia=${diaExport}&setor=${encodeURIComponent(setor)}`);
    const porAgenda = new Map();
    (Array.isArray(linhas) ? linhas : []).forEach((h) => {
      const chave = String(h.agenda_id);
      if (!porAgenda.has(chave)) porAgenda.set(chave, { agenda: h, pacientes: [] });
      porAgenda.get(chave).pacientes.push({ nome: TEXTO(h.paciente).trim(), telefone: TEXTO(h.contato).trim(), atendente: TEXTO(h.agendado_por).trim() });
    });
    const blocos = Array.from(porAgenda.values()).map(({ agenda: a, pacientes }) => ({
      data: dataBrExp(a.data),
      especialidade: a.especialidade || a.agenda_nome || 'Agenda',
      profissional: `${a.medico_nome || 'Profissional'} · ${a.hora_inicio || '—'}–${a.hora_fim || '—'}${a.intervalo ? ` · ${a.intervalo} min` : ''}`,
      pacientes
    }));
    const total = blocos.reduce((soma, b) => soma + b.pacientes.length, 0);
    if (!total) throw new Error('Nenhum agendamento foi feito neste dia.');
    return { blocos, total };
  };
  const exportarDia = async (formato) => {
    setExportando(true);
    setErroExport('');
    try {
      const { blocos, total } = await montarRelatorio();
      const cabecalho = `Setor ${titulo} · A partir de ${dataBrExp(diaExport)} · Total : ${total}`;
      const arquivo = `agendamentos-${setor}-${diaExport}`;
      const textoRelatorio = [
        `RELATÓRIO DE AGENDAMENTOS`,
        cabecalho,
        '',
        ...blocos.flatMap((b) => [
          `AGENDA DE ATENDIMENTOS · ${b.data}`,
          `Especialidade: ${b.especialidade}`,
          `Profissional: ${b.profissional}`,
          '',
          'Nome | Telefone | Atendente',
          ...b.pacientes.map((p) => `${p.nome || '—'} | ${p.telefone || '—'} | ${p.atendente || '—'}`),
          '',
          '----------------------------------------',
          ''
        ])
      ].join('\n');

      if (formato === 'txt') {
        const blob = new Blob([textoRelatorio], {
          type: 'text/plain;charset=utf-8'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${arquivo}.txt`;
        link.click();
        URL.revokeObjectURL(url);
      } else if (formato === 'excel') {
        const linhasXls = [[cabecalho], []];
        blocos.forEach((b) => {
          linhasXls.push([`Agenda de atendimentos · ${b.data}`], [b.especialidade], [b.profissional], ['Nome', 'Telefone', 'Atendente']);
          b.pacientes.forEach((p) => linhasXls.push([p.nome, p.telefone, p.atendente || '—']));
          linhasXls.push(['---'], []);
        });
        const ws = XLSX.utils.aoa_to_sheet(linhasXls);
        ws['!cols'] = [{ wch: 46 }, { wch: 22 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Agendamentos');
        XLSX.writeFile(wb, `${arquivo}.xlsx`);
      } else {
        const { jsPDF } = await import('jspdf');
        const autoTable = (await import('jspdf-autotable')).default;
        const doc = new jsPDF({ unit: 'mm', format: 'a4' });
        let y = 18;
        doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(20);
        doc.text(cabecalho, 14, y); y += 10;
        blocos.forEach((b, i) => {
          if (y > 240) { doc.addPage(); y = 18; }
          doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(110);
          doc.text(`Agenda de atendimentos · ${b.data}`, 14, y); y += 6;
          doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(20);
          doc.text(b.especialidade, 14, y); y += 5.5;
          doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(70);
          doc.text(b.profissional, 14, y); y += 4;
          autoTable(doc, {
            startY: y,
            head: [['Nome', 'Telefone', 'Atendente']],
            body: b.pacientes.map((p) => [p.nome, p.telefone || '—', p.atendente || '—']),
            theme: 'grid',
            styles: { fontSize: 9 },
            headStyles: { fillColor: [227, 240, 244], textColor: [52, 75, 88] },
            margin: { left: 14, right: 14 }
          });
          y = doc.lastAutoTable.finalY + 10;
          if (i < blocos.length - 1) { doc.setDrawColor(200); doc.line(14, y - 4, 196, y - 4); y += 4; }
        });
        doc.save(`${arquivo}.pdf`);
      }
      setModalExport(false);
      setModalRelatorio(false);
    } catch (e) {
      setErroExport(e.message || 'Não foi possível gerar o arquivo.');
    } finally { setExportando(false); }
  };

  const copiarRelatorioDia = async () => {
    setExportando(true);
    setErroExport('');

    try {
      const estado = mostrarArquivadas ? 'arquivada' : 'ativa';
      const partesHoje = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit'
      }).formatToParts(new Date());
      const hojeIso = `${partesHoje.find((parte) => parte.type === 'year')?.value}-${partesHoje.find((parte) => parte.type === 'month')?.value}-${partesHoje.find((parte) => parte.type === 'day')?.value}`;
      const resposta = await api(
        `/agenda-resumo?de=${PRIMEIRO_DIA(mes)}&ate=${ULTIMO_DIA(mes)}&setor=${encodeURIComponent(setor)}&estado=${estado}`
      );
      const agendasResumo = (Array.isArray(resposta) ? resposta : [])
        .filter((agenda) => TEXTO(agenda.estado || 'ativa').toLowerCase() === estado)
        .filter((agenda) => TEXTO(agenda.data).slice(0, 10) >= hojeIso)
        .sort((a, b) => TEXTO(a.especialidade || a.nome).localeCompare(TEXTO(b.especialidade || b.nome), 'pt-BR')
          || TEXTO(a.medico_nome || profissionaisPorId[String(a.medicoId ?? a.medico_id)]).localeCompare(
            TEXTO(b.medico_nome || profissionaisPorId[String(b.medicoId ?? b.medico_id)]), 'pt-BR'
          )
          || TEXTO(a.data).localeCompare(TEXTO(b.data)));

      if (!agendasResumo.length) throw new Error('Nenhuma agenda encontrada para copiar neste período.');

      const conteudos = [];
      for (let i = 0; i < agendasResumo.length; i += 6) {
        const lote = await Promise.all(agendasResumo.slice(i, i + 6).map(async (agenda) => {
          const horarios = await api(`/agenda-configuracoes/${agenda.id}/horarios`);
          return { agenda, horarios: Array.isArray(horarios) ? horarios : ARR(horarios) };
        }));
        conteudos.push(...lote);
      }

      const grupos = new Map();
      conteudos.forEach(({ agenda, horarios }) => {
        const profissionalNome = TEXTO(
          agenda.medico_nome || profissionaisPorId[String(agenda.medicoId ?? agenda.medico_id)] || agenda.nome || 'PROFISSIONAL'
        ).trim();
        const especialidade = TEXTO(agenda.especialidade || agenda.nome || 'AGENDA').trim();
        const chave = `${especialidade.toLocaleLowerCase('pt-BR')}::${profissionalNome.toLocaleLowerCase('pt-BR')}`;
        if (!grupos.has(chave)) grupos.set(chave, { especialidade, profissionalNome, datas: [] });

        const mapeamentoCol = ARR(agenda.colunas_extras).find((coluna) => {
          const nome = TEXTO(coluna?.nome || coluna?.label || coluna?.rotulo).normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '').toLowerCase();
          return nome.includes('mapeamento');
        });
        const agendamentos = horarios.filter((linha) => TEXTO(linha.paciente).trim() !== '').length;
        let mapeamento = null;
        if (mapeamentoCol) {
          const valores = horarios.map((linha) => TEXTO(OBJ(linha.dados_extras)[mapeamentoCol.id]).trim()).filter(Boolean);
          mapeamento = valores.reduce((total, valor) => {
            const numero = Number(valor.replace(',', '.'));
            return total + (Number.isFinite(numero) ? numero : 1);
          }, 0);
        }

        grupos.get(chave).datas.push({ data: TEXTO(agenda.data).slice(0, 10), agendamentos, mapeamento });
      });

      const blocosTexto = Array.from(grupos.values())
        .sort((a, b) => a.especialidade.localeCompare(b.especialidade, 'pt-BR')
          || a.profissionalNome.localeCompare(b.profissionalNome, 'pt-BR'))
        .map((grupo) => {
          const datas = grupo.datas.sort((a, b) => a.data.localeCompare(b.data));
          return [
            `${grupo.especialidade} - ${grupo.profissionalNome}`.toLocaleUpperCase('pt-BR'),
            ...datas.flatMap((item) => {
              const [ano, mesData, diaData] = item.data.split('-');
              const linha = `*${diaData}/${mesData}* - ${String(item.agendamentos).padStart(2, '0')} AGENDAMENTOS`;
              return item.mapeamento === null
                ? [linha]
                : [linha, `${String(item.mapeamento).padStart(2, '0')} MAPEAMENTO`];
            })
          ].join('\n');
        });

      const textoWhatsApp = [
        'Bom dia',
        '',
        'AGENDAS ATUALIZADAS 👨🏻‍⚕️👩🏼‍⚕️',
        '',
        blocosTexto.join('\n\n')
      ].join('\n');

      await navigator.clipboard.writeText(textoWhatsApp);
      setErroExport('Resumo para WhatsApp copiado com sucesso.');
    } catch (e) {
      setErroExport(e.message || 'Não foi possível copiar o resumo para WhatsApp.');
    } finally {
      setExportando(false);
    }
  };

  const carregarAgendas = useCallback(async () => {
    try {
      const dados = await api(`/agenda-resumo?de=${PRIMEIRO_DIA(mes)}&ate=${ULTIMO_DIA(mes)}&setor=${encodeURIComponent(setor)}&estado=${mostrarArquivadas ? 'arquivada' : 'ativa'}`);
      setAgendas(Array.isArray(dados) ? dados : []);
      setErro('');
    } catch (e) { setErro(e.message); setAgendas([]); }
    finally { setCarregando(false); }
  }, [mes, setor, mostrarArquivadas]);

  useEffect(() => { carregarCadastros(); }, [carregarCadastros]);
  useEffect(() => { carregarAgendas(); }, [carregarAgendas]);

  const profissionaisPorId = useMemo(() => Object.fromEntries(medicos.map((m) => [String(m.id), m.nome])), [medicos]);
  const gruposAgendas = useMemo(() => {
    const porChave = new Map();
    agendas.forEach((agenda) => {
      const chave = CHAVE_AGENDA(agenda);
      if (!porChave.has(chave)) {
        porChave.set(chave, {
          chave,
          medicoId: agenda.medicoId ?? agenda.medico_id,
          especialidade: agenda.especialidade || agenda.nome || 'Agenda',
          medicoNome: agenda.medico_nome || profissionaisPorId[String(agenda.medicoId ?? agenda.medico_id)] || agenda.nome || 'Profissional',
          blocos: [],
        });
      }
      porChave.get(chave).blocos.push(agenda);
    });
    return Array.from(porChave.values());
  }, [agendas, profissionaisPorId]);

  const grupoPorAgendaURL = useMemo(() => {
    if (!agendaIdURL) return null;

    const agendaEncontrada = agendas.find(
      (agenda) => String(agenda.id) === String(agendaIdURL)
    );

    if (!agendaEncontrada) return null;

    return gruposAgendas.find(
      (grupo) => grupo.blocos.some(
        (bloco) => String(bloco.id) === String(agendaIdURL)
      )
    ) || null;
  }, [agendaIdURL, agendas, gruposAgendas]);

  const agendasVisiveis = useMemo(() => {
    const q = buscaAplicada.trim().toLowerCase();
    return gruposAgendas.filter((grupo) => {
      if (filtroEspecialidade && grupo.especialidade !== filtroEspecialidade) return false;
      return !q || `${grupo.medicoNome} ${grupo.especialidade} ${grupo.blocos.map((a) => a.observacoes || '').join(' ')}`.toLowerCase().includes(q);
    });
  }, [gruposAgendas, buscaAplicada, filtroEspecialidade]);

  const blocosSelecionados = useMemo(() => grupoSelecionado
    ? agendas.filter((agenda) => CHAVE_AGENDA(agenda) === grupoSelecionado.chave)
    : [], [agendas, grupoSelecionado]);

  useEffect(() => {
    if (!chaveSelecionadaURL) {
      setGrupoSelecionado(null);
      return;
    }
    if (grupoPorAgendaURL) {
      setGrupoSelecionado(grupoPorAgendaURL);
      return;
    }

    const encontrado = gruposAgendas.find((grupo) => grupo.chave === chaveSelecionadaURL);
    if (encontrado) {
      setGrupoSelecionado(encontrado);
      return;
    }
    if (!carregando && grupoSelecionado?.chave !== chaveSelecionadaURL) {
      const [medicoId, ...especialidadePartes] = chaveSelecionadaURL.split('::');
      setGrupoSelecionado({
        chave: chaveSelecionadaURL,
        medicoId,
        especialidade: especialidadePartes.join('::') || 'Agenda',
        medicoNome: profissionaisPorId[String(medicoId)] || 'Profissional',
      });
    }
  }, [chaveSelecionadaURL, gruposAgendas, carregando, grupoSelecionado?.chave, profissionaisPorId]);

  const hojeIso = HOJE_ISO();

  // Usa a agenda de hoje quando existir.
  const blocoHoje = blocosSelecionados.find(
    (bloco) => TEXTO(bloco.data).slice(0, 10) === hojeIso
  );

  // Caso não exista agenda hoje, usa a próxima data futura mais próxima.
  const proximoBloco = [...blocosSelecionados]
    .map((bloco) => ({
      bloco,
      data: TEXTO(bloco.data).slice(0, 10),
    }))
    .filter((item) => item.data >= hojeIso)
    .sort((a, b) => a.data.localeCompare(b.data))[0]?.bloco || null;

  const diaAutomatico = blocoHoje
    ? hojeIso
    : proximoBloco
      ? TEXTO(proximoBloco.data).slice(0, 10)
      : hojeIso;

  const diaAtivo = diaEscolhidoManualmente
    ? (diaSel && diaSel.slice(0, 7) === mes ? diaSel : '')
    : diaAutomatico;

  const blocoDoDia = diaAtivo
    ? blocosSelecionados.find(
        (bloco) => TEXTO(bloco.data).slice(0, 10) === diaAtivo
      )
    : null;

  const escolherDia = (iso) => {
    if (!iso) return;

    setDiaEscolhidoManualmente(true);

    if (iso.slice(0, 7) !== mes) {
      setCarregando(true);
      selecionarMes(iso.slice(0, 7));
    }

    setDiaSel(iso);
    setBlocoId(null);
  };

  // "Excluir aba": só tira o mês da barra; nenhuma agenda é apagada do banco
  const excluirAba = () => {
    if (meses.length <= 1) { alert('Não é possível excluir a única aba de mês.'); return; }
    if (!window.confirm(`Remover a aba ${ROTULO_ABA_MES(mes)} da barra?\n\nNenhuma agenda será apagada. Para ver este mês de novo, adicione-o pelo botão +.`)) return;
    const restantes = meses.filter((m) => m !== mes);
    try { localStorage.setItem(`agendamentos-meses-ocultos-${setor}`, JSON.stringify(Array.from(new Set([...MESES_OCULTOS(setor), mes])))); } catch (_) { /* sem storage */ }
    setDiaSel('');
    setBlocoId(null);
    setMeses(restantes);
    setMes(restantes[0]);
  };

  const renomearAba = () => {
    const nome = window.prompt('Novo nome da aba (vazio restaura o padrão):', rotuloAba(mes));
    if (nome === null) return;
    const novos = { ...nomesAbas };
    if (nome.trim()) novos[mes] = nome.trim(); else delete novos[mes];
    setNomesAbas(novos);
    try { localStorage.setItem(`agendamentos-nomes-abas-${setor}`, JSON.stringify(novos)); } catch (_) { /* sem storage */ }
  };
  const iniciarEdicaoAba = (chave) => {
    setNomeAbaEdicao(rotuloAba(chave));
    setAbaRenomeando(chave);
  };
  const salvarEdicaoAba = (chave) => {
    const nome = nomeAbaEdicao.trim();
    const novos = { ...nomesAbas };
    if (nome) novos[chave] = nome; else delete novos[chave];
    setNomesAbas(novos);
    try { localStorage.setItem(`agendamentos-nomes-abas-${setor}`, JSON.stringify(novos)); } catch (_) { /* sem storage */ }
    setAbaRenomeando(null);
  };

  const abrirEdicaoAgenda = (grupo) => {
    const agenda = grupo.blocos[0];
    if (!agenda) return;

    setAgendaEditando(agenda);
    setForm({
      tipo: agenda.modalidade || 'Agenda médica',
      especialidade: agenda.especialidade || '',
      profissional: agenda.medico_nome || grupo.medicoNome || '',
      medicoId: String(agenda.medicoId ?? agenda.medico_id ?? ''),
      data: TEXTO(agenda.data).slice(0, 10),
      inicio: agenda.hora_inicio || '09:00',
      fim: agenda.hora_fim || '14:00',
      intervalo: agenda.intervalo || 20,
      valorAssociado: agenda.valor_cartao || '',
      valorParticular: agenda.valor_particular || '',
      cor: agenda.cor || '#11c9d3',
      consultorio_id: agenda.consultorio_id ? String(agenda.consultorio_id) : '',
      observacoes: agenda.observacoes || ''
    });
    setMenuAgendaAberto(null);
    setModalAgenda(true);
  };

  const duplicarAgenda = (grupo) => {
    const agenda = grupo?.blocos?.[0];
    if (!agenda) return;

    // Importante: null faz o salvamento usar POST,
    // criando uma nova agenda em vez de alterar a original.
    setAgendaEditando(null);

    setForm({
      tipo: agenda.modalidade || 'Agenda médica',
      especialidade: agenda.especialidade || '',
      profissional: agenda.medico_nome || grupo.medicoNome || '',
      medicoId: String(agenda.medicoId ?? agenda.medico_id ?? ''),
      data: TEXTO(agenda.data).slice(0, 10),
      inicio: agenda.hora_inicio || '09:00',
      fim: agenda.hora_fim || '14:00',
      intervalo: agenda.intervalo || 20,
      valorAssociado: agenda.valor_cartao || '',
      valorParticular: agenda.valor_particular || '',
      cor: agenda.cor || '#11c9d3',
      consultorio_id: agenda.consultorio_id
        ? String(agenda.consultorio_id)
        : '',
      observacoes: agenda.observacoes || ''
    });

    setMenuAgendaAberto(null);
    setModalAgenda(true);
  };

  const alterarEstadoGrupo = async (grupo, estado) => {
    const arquivando = estado === 'arquivada';
    const mensagem = arquivando
      ? `Arquivar a agenda “${grupo.especialidade} · ${grupo.medicoNome}”? Os horários serão preservados.`
      : `Restaurar a agenda “${grupo.especialidade} · ${grupo.medicoNome}”?`;

    if (!window.confirm(mensagem)) return false;

    try {
      await Promise.all(grupo.blocos.map((agenda) =>
        api(`/agenda-configuracoes/${agenda.id}/estado`, {
          method: 'POST',
          body: JSON.stringify({ estado })
        })
      ));
      setMenuAgendaAberto(null);
      await carregarAgendas();
      return true;
    } catch (err) {
      setErro(`Não foi possível ${arquivando ? 'arquivar' : 'restaurar'} a agenda: ${err.message}`);
    }
  };

  const excluirGrupo = async (grupo) => {
    if (!window.confirm(`Excluir definitivamente a agenda “${grupo.especialidade} · ${grupo.medicoNome}”? Os horários também serão apagados.`)) return;

    try {
      await Promise.all(grupo.blocos.map((agenda) =>
        api(`/agenda-configuracoes/${agenda.id}`, { method: 'DELETE' })
      ));
      setMenuAgendaAberto(null);
      await carregarAgendas();
    } catch (err) {
      setErro(`Não foi possível excluir a agenda: ${err.message}`);
    }
  };

  const corDoGrupo = (grupo) => {
    const cor = TEXTO(grupo?.blocos?.[0]?.cor);
    return /^#[0-9a-f]{6}$/i.test(cor) ? cor.toUpperCase() : '#4D56D8';
  };

  const opcoesEspecialidade = Array.from(new Set(
    gruposAgendas.map((g) => String(g.especialidade || '').trim()).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const limparFiltros = () => {
    setBusca('');
    setBuscaAplicada('');
    setFiltroEspecialidade('');
  };

  const diasDoGrupo = (grupo) => {
    const blocos = grupo?.blocos || [];
    const datas = new Set(blocos.map((bloco) => TEXTO(bloco.data).slice(0, 10)).filter(Boolean));
    return datas.size || blocos.length;
  };
  const proximaDataDoGrupo = (grupo) => {
    const partesHoje = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date());

    const hojeIso = `${partesHoje.find((parte) => parte.type === 'year')?.value}-${partesHoje.find((parte) => parte.type === 'month')?.value}-${partesHoje.find((parte) => parte.type === 'day')?.value}`;

    const proximaData = (grupo?.blocos || [])
      .map((bloco) => TEXTO(bloco.data).slice(0, 10))
      .filter((data) => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(data)) return false;

        const ano = Number(data.slice(0, 4));
        return ano >= 2000 && ano <= 2100 && data >= hojeIso;
      })
      .sort()[0];

    return proximaData ? isoParaBR(proximaData) : '';
  };

  const abrirCorGrupo = (grupo) => {
    setCorGrupoAberto(grupo);
    setCorGrupoValor(corDoGrupo(grupo));
    setMenuAgendaAberto(null);
  };

  const salvarCorGrupo = async () => {
    if (!corGrupoAberto) return;
    setSalvandoCorGrupo(true);
    try {
      await Promise.all(corGrupoAberto.blocos.map((agenda) =>
        api(`/agenda-configuracoes/${agenda.id}`, {
          method: 'PUT',
          body: JSON.stringify({ cor: corGrupoValor })
        })
      ));
      setCorGrupoAberto(null);
      await carregarAgendas();
    } catch (err) {
      setErro(`Não foi possível trocar a cor da agenda: ${err.message}`);
    } finally {
      setSalvandoCorGrupo(false);
    }
  };

  const abrirNovaAgenda = () => {
    setAgendaEditando(null);
    setForm({
      tipo: 'Agenda médica',
      especialidade: '',
      profissional: '',
      medicoId: '',
      data: PRIMEIRO_DIA(mes),
      inicio: '09:00',
      fim: '14:00',
      intervalo: 20,
      valorAssociado: '',
      valorParticular: '',
      cor: '#11c9d3',
      consultorio_id: '',
      observacoes: ''
    });
    setModalAgenda(true);
  };

  const salvarEspecialidade = async (e) => {
    e.preventDefault();
    if (!novaEspecialidade.trim()) return;
    try {
      await api('/especialidades', { method: 'POST', body: JSON.stringify({ nome: novaEspecialidade.trim(), setor }) });
      await carregarCadastros();
      setFiltroEspecialidade(novaEspecialidade.trim());
      setNovaEspecialidade('');
      setModalEspecialidade(false);
    } catch (err) { setErro(err.message); }
  };

  useEffect(() => {
    if (!abrirNovaURL || carregando) return;

    abrirNovaAgenda();

    const params = new URLSearchParams(searchParams);
    params.delete('nova');
    setSearchParams(params, { replace: true });
  }, [abrirNovaURL, carregando]);

  const salvarAgenda = async (e) => {
    e.preventDefault();
    let nome = form.profissional.trim();
    let especialidade = form.especialidade.trim();
    const pareceNomeProfissional = (valor) => /^\s*dra?\.?\s/i.test(valor);
    if (pareceNomeProfissional(especialidade) && !pareceNomeProfissional(nome)) {
      const inverter = window.confirm(
        `Parece que o nome do profissional ("${especialidade}") foi digitado em Especialidade, ` +
        `e a especialidade ("${nome}") em Profissional responsável.\n\nDeseja inverter os campos antes de salvar?`
      );
      if (inverter) [nome, especialidade] = [especialidade, nome];
    }
    if (!nome || !especialidade || !form.data) { setErro('Informe profissional, especialidade e data.'); return; }
    try {
      setSalvando(true);

      if (agendaEditando) {

        await api(`/agenda-configuracoes/${agendaEditando.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            nome: `Agenda - ${nome}`,
            profissional_nome: nome,
            especialidade,
            data: form.data,
            hora_inicio: form.inicio,
            hora_fim: form.fim,
            intervalo: Number(form.intervalo),
            valor_cartao: form.valorAssociado.trim(), valor_particular: form.valorParticular.trim(),
            modalidade: form.tipo,
            dia_clinica: new Date(`${form.data}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long' }),
            observacoes: form.observacoes.trim(),
            cor: form.cor || null,
            consultorio_id: form.consultorio_id
              ? Number(form.consultorio_id)
              : null
          })
        });

        const mesDestino = form.data.slice(0, 7);
        setMeses((atuais) => Array.from(new Set([mesDestino, ...atuais])).sort().reverse());
        setMes(mesDestino);
        setAgendaEditando(null);
        setModalAgenda(false);

        // Atualiza o cadastro do médico e depois as agendas exibidas.
        await carregarCadastros();
        await carregarAgendas();

        return;
      }

      let medico = medicos.find((m) => String(m.id) === String(form.medicoId)) || medicos.find((m) => m.nome.toLowerCase() === nome.toLowerCase());
      if (!medico) medico = await api('/medicos', { method: 'POST', body: JSON.stringify({ nome, especialidade, setor }) });
      const criada = await api('/agenda-configuracoes', {
        method: 'POST',
        body: JSON.stringify({
          medicoId: Number(medico.id), nome: `Agenda - ${medico.nome || nome}`, especialidade,
          data: form.data, hora_inicio: form.inicio, hora_fim: form.fim, intervalo: Number(form.intervalo),
          valor_cartao: form.valorAssociado.trim(), valor_particular: form.valorParticular.trim(), modalidade: form.tipo,
          dia_clinica: new Date(`${form.data}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long' }),
          observacoes: form.observacoes.trim(),
          cor: form.cor || null,
          consultorio_id: form.consultorio_id
            ? Number(form.consultorio_id)
            : null
        })
      });
      await api(`/agenda-configuracoes/${criada.id}/horarios/gerar`, { method: 'POST', body: JSON.stringify({ inicio: form.inicio, fim: form.fim, intervalo: Number(form.intervalo) }) });
      const mesDestino = form.data.slice(0, 7);

      setMeses((atuais) =>
        Array.from(new Set([mesDestino, ...atuais])).sort().reverse()
      );
      setMes(mesDestino);

      /*
       * Recarrega antes de abrir o detalhe.
       * Isso evita que o grupo selecionado fique sem blocos
       * e resulte em uma tela branca.
       */
      if (mesDestino === mes) {
        await carregarAgendas();
      }

      const chaveCriada = CHAVE_AGENDA({
        medicoId: medico.id,
        especialidade
      });

      const parametrosDetalhe = new URLSearchParams(searchParams);
      parametrosDetalhe.set('agenda', chaveCriada);
      parametrosDetalhe.set('agendaId', String(criada.id));
      parametrosDetalhe.set('dia', form.data);

      setAgendaEditando(null);
      setModalAgenda(false);
      setMenuAgendaAberto(null);
      setGrupoSelecionado(null);
      setDiaSel(form.data);
      setDiaEscolhidoManualmente(true);
      setBlocoId(null);
      setSearchParams(parametrosDetalhe);

      await carregarCadastros();
    } catch (err) { setErro(err.message); }
    finally { setSalvando(false); }
  };

  const selecionarMes = (novoMes) => {
    if (!novoMes) return;
    setMeses((atuais) => Array.from(new Set([novoMes, ...atuais])).sort().reverse());
    setMes(novoMes);
  };

  const exportarExcel = async () => {
    if (!podeExportar) return;
    setExportandoExcel(true);
    try {
      const estado = mostrarArquivadas ? 'arquivada' : 'ativa';
      let todas;
      try {
        todas = await api(`/agenda-resumo?de=${PRIMEIRO_DIA(mes)}&ate=${ULTIMO_DIA(mes)}&setor=${encodeURIComponent(setor)}&estado=${estado}`);
      } catch (_) {
        todas = agendas;
      }
      todas = Array.isArray(todas) ? todas : [];

      const nomeProf = (agenda) => profissionaisPorId[String(agenda.medicoId ?? agenda.medico_id)] || agenda.medico_nome || '';
      const termo = buscaAplicada.trim().toLowerCase();
      const filtradas = todas
        .filter((agenda) => !filtroEspecialidade || String(agenda.especialidade || '').trim() === filtroEspecialidade)
        .filter((agenda) => !termo || `${nomeProf(agenda)} ${agenda.especialidade || ''}`.toLowerCase().includes(termo))
        .sort((a, b) => nomeProf(a).localeCompare(nomeProf(b), 'pt-BR') || TEXTO(a.data).localeCompare(TEXTO(b.data)));

      if (!filtradas.length) { setErro('Nenhuma agenda para exportar com os filtros atuais.'); return; }

      const conteudos = [];
      for (let i = 0; i < filtradas.length; i += 6) {
        const lote = await Promise.all(filtradas.slice(i, i + 6).map(async (agenda) => {
          try {
            const linhas = await api(`/agenda-configuracoes/${agenda.id}/horarios`);
            return { agenda, linhas: Array.isArray(linhas) ? linhas : ARR(linhas) };
          } catch (_) {
            return { agenda, linhas: [] };
          }
        }));
        conteudos.push(...lote);
      }

      const nomeConsultorio = (agenda) => (consultorios || []).find((c) => String(c.id) === String(agenda.consultorio_id))?.nome || '';
      const diaSemana = (data) => {
        const iso = TEXTO(data).slice(0, 10);
        if (!iso) return '';
        const dia = new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long' });
        return dia.charAt(0).toUpperCase() + dia.slice(1);
      };
      const rotuloStatus = (valor) => (STATUS.find(([v]) => v === valor) || [])[1] || valor || '';

      const mapaExtras = new Map();
      conteudos.forEach(({ agenda }) => ARR(agenda.colunas_extras).forEach((coluna) => mapaExtras.set(coluna.id, coluna)));
      const extras = Array.from(mapaExtras.values());

      const cabecalhoHorarios = ['Profissional', 'Especialidade', 'Data', 'Dia', 'Consultório', ...CAMPOS.map(([campo, rotulo]) => rotuloCampo(campo, rotulo, setor)), 'Status', ...extras.map((coluna) => coluna.nome || 'Coluna')];
      const linhasHorarios = conteudos.flatMap(({ agenda, linhas }) => linhas.map((linha) => [
        nomeProf(agenda),
        agenda.especialidade || '',
        isoParaBR(agenda.data),
        diaSemana(agenda.data),
        nomeConsultorio(agenda),
        ...CAMPOS.map(([campo]) => TEXTO(linha[campo] ?? '')),
        rotuloStatus(linha.status),
        ...extras.map((coluna) => TEXTO(OBJ(linha.dados_extras)[coluna.id] ?? '')),
      ]));

      const cabecalhoResumo = ['Profissional', 'Especialidade', 'Data', 'Dia', 'Início', 'Fim', 'Intervalo (min)', 'Consultório', 'Horários', 'Pacientes marcados'];
      const linhasResumo = conteudos.map(({ agenda, linhas }) => [
        nomeProf(agenda),
        agenda.especialidade || '',
        isoParaBR(agenda.data),
        diaSemana(agenda.data),
        agenda.hora_inicio || '',
        agenda.hora_fim || '',
        agenda.intervalo ?? '',
        nomeConsultorio(agenda),
        linhas.length,
        linhas.filter((linha) => TEXTO(linha.paciente).trim()).length,
      ]);

      const montarAba = (cabecalho, linhas) => {
        const aba = XLSX.utils.aoa_to_sheet([cabecalho, ...linhas]);
        aba['!cols'] = cabecalho.map((titulo, indice) => ({
          wch: Math.min(40, Math.max(10, titulo.length + 2, ...linhas.map((l) => TEXTO(l[indice]).length + 2)))
        }));
        return aba;
      };

      const livro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(livro, montarAba(cabecalhoResumo, linhasResumo), 'Resumo');

      // Uma aba por profissional (mesmo agrupamento dos cards), com todos os dias dele no mês.
      const porProfissional = new Map();
      conteudos.forEach((conteudo) => {
        const chave = `${nomeProf(conteudo.agenda)}::${conteudo.agenda.especialidade || ''}`;
        if (!porProfissional.has(chave)) porProfissional.set(chave, []);
        porProfissional.get(chave).push(conteudo);
      });

      const nomesUsados = new Set(['resumo', 'todos os horários']);
      const nomeAba = (base) => {
        const limpo = String(base || 'Profissional').replace(/[\[\]:*?\/\\]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 28) || 'Profissional';
        let nome = limpo;
        let numero = 2;
        while (nomesUsados.has(nome.toLowerCase())) {
          nome = `${limpo.slice(0, 25)} ${numero}`;
          numero += 1;
        }
        nomesUsados.add(nome.toLowerCase());
        return nome;
      };

      Array.from(porProfissional.values()).forEach((lista) => {
        const ordenada = [...lista].sort((a, b) => TEXTO(a.agenda.data).localeCompare(TEXTO(b.agenda.data)));
        const primeira = ordenada[0].agenda;

        const extrasProf = new Map();
        ordenada.forEach(({ agenda }) => ARR(agenda.colunas_extras).forEach((coluna) => extrasProf.set(coluna.id, coluna)));
        const colunasExtras = Array.from(extrasProf.values());

        const cabecalhoProf = ['Data', 'Dia', 'Consultório', ...CAMPOS.map(([campo, rotulo]) => rotuloCampo(campo, rotulo, setor)), 'Status', ...colunasExtras.map((coluna) => coluna.nome || 'Coluna')];
        const linhasProf = ordenada.flatMap(({ agenda, linhas }) => linhas.map((linha) => [
          isoParaBR(agenda.data),
          diaSemana(agenda.data),
          nomeConsultorio(agenda),
          ...CAMPOS.map(([campo]) => TEXTO(linha[campo] ?? '')),
          rotuloStatus(linha.status),
          ...colunasExtras.map((coluna) => TEXTO(OBJ(linha.dados_extras)[coluna.id] ?? '')),
        ]));

        const titulo = `${nomeProf(primeira) || 'Profissional'}${primeira.especialidade ? ` - ${primeira.especialidade}` : ''}`;
        XLSX.utils.book_append_sheet(livro, montarAba(cabecalhoProf, linhasProf), nomeAba(titulo));
      });

      XLSX.utils.book_append_sheet(livro, montarAba(cabecalhoHorarios, linhasHorarios), 'Todos os horários');
      XLSX.writeFile(livro, `agendas-${setor}${mostrarArquivadas ? '-arquivadas' : ''}-${mes}.xlsx`);
    } catch (e) {
      setErro(`Não foi possível exportar: ${e.message}`);
    } finally {
      setExportandoExcel(false);
    }
  };

  const exportarCSV = async () => {
    try {
      const conteudos = await Promise.all(blocosSelecionados.map(async (agenda) => ({
        agenda,
        linhas: await api(`/agenda-configuracoes/${agenda.id}/horarios`),
      })));
      const mapaExtras = new Map();
      conteudos.forEach(({ agenda }) => ARR(agenda.colunas_extras).forEach((coluna) => mapaExtras.set(coluna.id, coluna)));
      const extras = Array.from(mapaExtras.values());
      const cabecalho = ['Setor', 'Especialidade', 'Profissional', 'Data da agenda', ...CAMPOS.map(([campo, rotulo]) => rotuloCampo(campo, rotulo, setor)), 'Status', ...extras.map((coluna) => coluna.nome || 'Coluna')];
      const escapar = (valor) => `"${TEXTO(valor).replace(/"/g, '""')}"`;
      const linhasCSV = conteudos.flatMap(({ agenda, linhas }) => (Array.isArray(linhas) ? linhas : []).map((linha) => [
        titulo,
        agenda.especialidade || agenda.nome || '',
        profissionaisPorId[String(agenda.medicoId ?? agenda.medico_id)] || agenda.medico_nome || '',
        isoParaBR(agenda.data),
        ...CAMPOS.map(([campo]) => linha[campo] ?? ''),
        linha.status || '',
        ...extras.map((coluna) => OBJ(linha.dados_extras)[coluna.id] ?? ''),
      ].map(escapar).join(';')));
      const csv = `\uFEFF${[cabecalho.map(escapar).join(';'), ...linhasCSV].join('\r\n')}`;
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `agendamentos-${setor}-${mes}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErro(`Não foi possível exportar: ${e.message}`); }
  };

  return (
    <main className={`agset-page ${grupoSelecionado ? 'agset-page-detail' : 'agset-page-list'}`}>
      {!grupoSelecionado ? (
        <>
          <header className="agset-page-header">
            <div><span className="agset-kicker">Central de agendas</span><h1><CalendarDays size={23} /> Agendamentos · {titulo}</h1><p>Pesquise profissionais e especialidades para abrir uma agenda.</p></div>
          </header>
          {erro && <div className="agset-error" role="alert">{erro}<button type="button" onClick={() => setErro('')} aria-label="Fechar erro"><X size={14} /></button></div>}
          <div className="agset-list-block"><div className="agtb-export-row">
              {podeRelatorio && (
                <button
                  type="button"
                  className="agtb-btn agtb-btn-report"
                  onClick={abrirRelatorio}
                  title="Relatório de agendamentos"
                >
                  <FileDown size={15} /> Relatório
                </button>
              )}
              {podeExportar && (
                <button
                  type="button"
                  className="agtb-btn agtb-btn-outline"
                  onClick={exportarExcel}
                  disabled={exportandoExcel}
                >
                  <FileSpreadsheet size={15} /> {exportandoExcel ? 'Exportando…' : 'Exportar Excel'}
                </button>
              )}
            </div><form className="agtb" onSubmit={(e) => { e.preventDefault(); setBuscaAplicada(busca); }}>
            <div className="agtb-left">
              <label className="agtb-search"><Search size={16} /><input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Pesquisar profissional ou especialidade..." aria-label="Pesquisar profissional ou especialidade" /></label>
              <select className="agtb-select" aria-label="Especialidade" value={opcoesEspecialidade.includes(filtroEspecialidade) ? filtroEspecialidade : ''} onChange={(e) => setFiltroEspecialidade(e.target.value)}>
                <option value="">Especialidade</option>
                {opcoesEspecialidade.map((esp) => <option key={esp} value={esp}>{esp}</option>)}
              </select>
              <button type="button" className="agtb-clear" onClick={limparFiltros}>Limpar Filtros</button>
              <button type="submit" className="agtb-btn agtb-btn-buscar"><Search size={15} /> Buscar</button>
              <button type="button" className="agtb-btn agtb-btn-new" onClick={abrirNovaAgenda}><Plus size={16} /> Nova agenda</button>
            </div>
            <div className="agtb-gestao">
              <div className="agset-exp-topo">
                <span className="agtb-gestao-titulo">Gestão de Agendas</span>
                {podeBaixarDia && (
                  <button
                    type="button"
                    className="agset-exp-btn"
                    title="Baixar agendamentos do dia"
                    aria-label="Baixar agendamentos do dia"
                    onClick={abrirExport}
                  >
                    <FileDown size={17} />
                  </button>
                )}
              </div>
              <div className="agtb-gestao-acoes">
                {setor === 'odontologia' && <button type="button" className="agtb-btn agtb-btn-outline agtb-btn-sm" onClick={() => navegar(`/agendamentos/${setor}/consultorio`)}><Building2 size={14} /> Consultório</button>}
                <button type="button" className="agtb-btn agtb-btn-outline agtb-btn-sm" onClick={() => { setMostrarArquivadas((valor) => !valor); setMenuAgendaAberto(null); }}>
                  {mostrarArquivadas ? <><RotateCcw size={14} /> Ativas</> : <><Archive size={14} /> Arquivadas</>}
                </button>
              </div>
            </div>
          </form>
          <section className="agset-agenda-list" aria-label="Agendas encontradas">
            {carregando ? <div className="agset-empty-panel">Carregando agendas…</div> : agendasVisiveis.length ? agendasVisiveis.map((grupo, index) => (
              <div className="agset-agenda-card-wrap" key={grupo.chave} style={{ '--agset-card-color': corDoGrupo(grupo) }}>
                  <button type="button" className="agset-agenda-card" onClick={() => { setGrupoSelecionado(grupo); const params = new URLSearchParams(searchParams); params.set('agenda', grupo.chave); setSearchParams(params); }}>
                    <span className="agset-agenda-card-top"><span className="agset-agenda-card-icon"><CalendarDays size={16} /></span><span className="agset-agenda-card-kicker">{String(index + 1).padStart(2, '0')} · AGENDA</span></span>
                    <strong>{grupo.medicoNome}</strong>
                    <span className="agset-agenda-card-meta">{String(grupo.especialidade || '').split('::').join(' · ')} · {diasDoGrupo(grupo)} {diasDoGrupo(grupo) === 1 ? 'dia' : 'dias'}</span>
                    {(() => {
                      const proximaData = proximaDataDoGrupo(grupo);
                      return proximaData ? <span className="agset-agenda-card-next-date">{proximaData}</span> : null;
                    })()}
                    <ChevronRight className="agset-agenda-card-arrow" size={18} />
                  </button>
                  <button
                    type="button"
                    className="agset-agenda-card-menu-button"
                    aria-label={`Ações da agenda ${grupo.especialidade}`}
                    onClick={(evento) => {
                      evento.stopPropagation();
                      setMenuAgendaAberto((atual) => atual === grupo.chave ? null : grupo.chave);
                    }}
                  >
                    <MoreVertical size={17} />
                  </button>
                  {menuAgendaAberto === grupo.chave && (
                    <div className="agset-agenda-card-menu" onClick={(evento) => evento.stopPropagation()}>
                      {!mostrarArquivadas && (
                        <>
                          <button type="button" onClick={() => duplicarAgenda(grupo)}>
                            <Copy size={14} /> Duplicar agenda
                          </button>
                          <button type="button" onClick={() => abrirEdicaoAgenda(grupo)}>
                            <Edit3 size={14} /> Editar
                          </button>
                          <button type="button" onClick={() => abrirCorGrupo(grupo)}>
                            <Palette size={14} /> Trocar cor
                          </button>
                          <button type="button" onClick={() => alterarEstadoGrupo(grupo, 'arquivada')}>
                            <Archive size={14} /> Inativar
                          </button>
                        </>
                      )}
                      {mostrarArquivadas && (
                        <button type="button" onClick={() => alterarEstadoGrupo(grupo, 'ativa')}>
                          <RotateCcw size={14} /> Ativar
                        </button>
                      )}
                      <button type="button" className="danger" onClick={() => excluirGrupo(grupo)}>
                        <Trash2 size={14} /> Excluir
                      </button>
                    </div>
                  )}
                </div>
            )) : <div className="agset-empty-panel"><h2>Nenhuma agenda encontrada em {ROTULO_MES(mes)}</h2><p>Cadastre uma agenda para este setor ou ajuste os filtros.</p></div>}
          </section></div>
          
        </>
      ) : (
        <>
          {erro && <div className="agset-error" role="alert">{erro}<button type="button" onClick={() => setErro('')} aria-label="Fechar erro"><X size={14} /></button></div>}
          <div className="agset-detail-content">
            {carregando ? <div className="agset-empty-panel">Carregando blocos da agenda…</div> : (diaAtivo && !blocoDoDia) ? <DiaVazio onVoltar={() => { setDiaSel(''); setBlocoId(null); const atual = MES_ATUAL(); if (meses.includes(atual)) setMes(atual); const params = new URLSearchParams(searchParams); params.delete('agenda'); setSearchParams(params); setGrupoSelecionado(null); }} grupoSelecionado={grupoSelecionado} gruposAgendas={gruposAgendas} dia={diaAtivo} onEscolherDia={escolherDia} diasComAgenda={blocosSelecionados.map((b) => TEXTO(b.data).slice(0, 10))} onTrocarGrupo={(chave) => { const grupo = gruposAgendas.find((item) => item.chave === chave); if (!grupo) return; setGrupoSelecionado(grupo); const params = new URLSearchParams(searchParams); params.set('agenda', grupo.chave); setSearchParams(params); }} /> : blocosSelecionados.length ? [blocoDoDia || blocosSelecionados.find((b) => String(b.id) === String(blocoId)) || null]
              .filter(Boolean)
              .map((agenda, index) => (
              <BlocoAgenda
                key={agenda.id}
                setor={setor}
                agenda={agenda}
                profissional={profissionaisPorId[String(agenda.medicoId ?? agenda.medico_id)] || grupoSelecionado.medicoNome}
                onAtualizar={carregarAgendas}
                onNovaAgenda={abrirNovaAgenda}
                onDuplicar={async (data) => { const mesDestino = data.slice(0, 7); selecionarMes(mesDestino); if (mesDestino === mes) await carregarAgendas(); }}
                mostrarControles={index === 0}
                blocosDatas={blocosSelecionados}
                onTrocarBloco={setBlocoId}
                onEscolherDia={escolherDia}
                gruposAgendas={gruposAgendas}
                grupoSelecionado={grupoSelecionado}
                mes={mes}
                onArquivar={async () => { if (await alterarEstadoGrupo(grupoSelecionado, 'arquivada')) { setDiaSel(''); setBlocoId(null); const params = new URLSearchParams(searchParams); params.delete('agenda'); setSearchParams(params); setGrupoSelecionado(null); } }}
                onSelecionarMes={selecionarMes}
                onTrocarGrupo={(chave) => {
                  const grupo = gruposAgendas.find((item) => item.chave === chave);
                  if (!grupo) return;
                  setGrupoSelecionado(grupo);
                  const params = new URLSearchParams(searchParams);
                  params.set('agenda', grupo.chave);
                  setSearchParams(params);
                }}
                onVoltar={() => {
                  setDiaSel(''); setBlocoId(null); { const atual = MES_ATUAL(); if (meses.includes(atual)) setMes(atual); }
                  const params = new URLSearchParams(searchParams);
                  params.delete('agenda');
                  setSearchParams(params);
                  setGrupoSelecionado(null);
                }}
              />
            )) : <div className="agset-empty-panel"><h2>Nenhum bloco em {ROTULO_MES(mes)}</h2><p>Escolha outro mês ou volte à lista de agendas.</p><button type="button" className="agset-secondary" onClick={() => { const params = new URLSearchParams(searchParams); params.delete('agenda'); setSearchParams(params); setGrupoSelecionado(null); }}>Voltar às agendas</button></div>}
          </div>
          <footer className={`agset-month-tabs ${abasAbertas ? "agset-tabs-open" : "agset-tabs-collapsed"}`} onClickCapture={(e) => { if (e.target.closest && e.target.closest(".agset-monthpicker-trigger")) setAbasAbertas((v) => !v); }}><MonthPickerBR value={mes} onChange={(m) => { setDiaSel(''); setBlocoId(null); selecionarMes(m); }} compact onExcluir={excluirAba} onRenomear={renomearAba} podeExcluir={meses.length > 1} />{meses.map((item) => {
            const estiloAba = { '--agset-month-accent': mes === item ? '#42a477' : '#d18892' };
            if (abaRenomeando === item) return <span key={item} style={estiloAba} className={`agset-month-tab ${mes === item ? 'active' : ''} agset-month-tab-editing`}>
              <input
                autoFocus
                aria-label="Nome da aba de mês"
                value={nomeAbaEdicao}
                onChange={(evento) => setNomeAbaEdicao(evento.target.value)}
                onClick={(evento) => evento.stopPropagation()}
                onKeyDown={(evento) => {
                  evento.stopPropagation();
                  if (evento.key === 'Enter') { evento.preventDefault(); evento.currentTarget.blur(); }
                  if (evento.key === 'Escape') { evento.preventDefault(); evento.currentTarget.dataset.cancelado = '1'; setAbaRenomeando(null); }
                }}
                onBlur={(evento) => {
                  if (evento.currentTarget.dataset.cancelado === '1') { delete evento.currentTarget.dataset.cancelado; return; }
                  salvarEdicaoAba(item);
                }}
              />
            </span>;
            return <button
              type="button"
              key={item}
              style={estiloAba}
              className={`agset-month-tab ${mes === item ? 'active' : ''}`}
              title="Duplo clique para renomear"
              onClick={() => { setDiaSel(''); setBlocoId(null); setMes(item); }}
              onDoubleClick={(evento) => { evento.preventDefault(); evento.stopPropagation(); iniciarEdicaoAba(item); }}
            >{rotuloAba(item)}</button>;
          })}</footer>
        </>
      )}

      {corGrupoAberto && <div className="agset-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !salvandoCorGrupo) setCorGrupoAberto(null); }}>
        <div className="agset-color-modal" role="dialog" aria-label="Trocar cor da agenda">
          <span className="agset-color-modal-eyebrow">Organização</span>
          <h2>Cor da agenda</h2>
          <p>{corGrupoAberto.especialidade} · {corGrupoAberto.medicoNome}</p>
          <div className="agset-card-colors" role="radiogroup" aria-label="Cor da agenda">
            {CORES_CARD_AGENDA.map((cor) => <button key={cor} type="button" role="radio" aria-checked={corGrupoValor === cor} aria-label={`Cor ${cor}`} className={`agset-card-color${corGrupoValor === cor ? ' is-selected' : ''}`} style={{ background: cor }} onClick={() => setCorGrupoValor(cor)}>{corGrupoValor === cor ? '✓' : ''}</button>)}
            <label className={`agset-card-color-custom${!CORES_CARD_AGENDA.includes(corGrupoValor) ? ' is-selected' : ''}`} title="Escolher outra cor" style={!CORES_CARD_AGENDA.includes(corGrupoValor) ? { background: corGrupoValor } : undefined}>
              <input type="color" value={corGrupoValor} onChange={(e) => setCorGrupoValor(e.target.value.toUpperCase())} aria-label="Escolher outra cor" /><span>+</span>
            </label>
          </div>
          <div className="agset-color-modal-footer">
            <button type="button" className="agset-color-cancel" onClick={() => setCorGrupoAberto(null)} disabled={salvandoCorGrupo}>Cancelar</button>
            <button type="button" className="agset-color-save" onClick={salvarCorGrupo} disabled={salvandoCorGrupo}>{salvandoCorGrupo ? 'Salvando…' : 'Salvar cor'}</button>
          </div>
        </div>
      </div>}
      {modalAgenda && <div className="agset-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setModalAgenda(false); }}><form className="agset-modal" onSubmit={salvarAgenda}><div className="agset-modal-head"><div><h2>{agendaEditando ? 'Editar agenda' : 'Nova agenda'}</h2><p>{agendaEditando ? 'Atualize os dados desta agenda.' : 'Defina profissional, especialidade e horários.'}</p></div><button type="button" className="agset-icon-button" onClick={() => setModalAgenda(false)} aria-label="Fechar"><X size={18} /></button></div><div className="agset-modal-grid"><label>Consultório<select value={form.consultorio_id || ''} onChange={(e) => setForm({ ...form, consultorio_id: e.target.value })}><option value="">Selecione o consultório</option>{consultorios.map((consultorio) => <option key={consultorio.id} value={consultorio.id}>{consultorio.nome}</option>)}</select></label><label>Tipo de agenda<select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>{TIPOS.map((t) => <option key={t}>{t}</option>)}</select></label><label>Especialidade / Serviço<input list={`agset-especialidades-${setor}`} value={form.especialidade} onChange={(e) => setForm({ ...form, especialidade: e.target.value })} placeholder="Ex.: Ortodontia" /><datalist id={`agset-especialidades-${setor}`}>{especialidades.map((esp) => <option key={esp} value={esp} />)}</datalist></label><label className="wide">Profissional responsável<input list={`agset-medicos-${setor}`} value={form.profissional} onChange={(e) => { const nome = e.target.value; const encontrado = medicos.find((m) => m.nome.toLowerCase() === nome.toLowerCase()); setForm({ ...form, profissional: nome, medicoId: encontrado ? String(encontrado.id) : '', especialidade: encontrado ? encontrado.especialidade : form.especialidade }); }} placeholder="Digite ou selecione o profissional" /><datalist id={`agset-medicos-${setor}`}>{medicos.map((m) => <option key={m.id} value={m.nome} />)}</datalist></label><div className="agset-modal-row wide"><label>Data<DateInputBR value={form.data} onChange={(data) => setForm({ ...form, data })} required /></label><label>Primeiro horário<HoraInput24 value={form.inicio} onChange={(valor) => setForm((atual) => ({ ...atual, inicio: valor }))} /></label><label>Último horário<HoraInput24 value={form.fim} onChange={(valor) => setForm((atual) => ({ ...atual, fim: valor }))} /></label></div><div className="agset-modal-row wide"><label>Intervalo<select value={form.intervalo} onChange={(e) => setForm({ ...form, intervalo: e.target.value })}>{[10, 15, 20, 30, 40, 60].map((v) => <option key={v} value={v}>{v} minutos</option>)}</select></label><label>Valor associado<input inputMode="decimal" value={form.valorAssociado} onChange={(e) => setForm({ ...form, valorAssociado: e.target.value })} placeholder="Ex.: 49,90" /></label><label>Valor particular<input inputMode="decimal" value={form.valorParticular} onChange={(e) => setForm({ ...form, valorParticular: e.target.value })} placeholder="Ex.: 120,00" /></label></div><div className="agset-modal-row wide agset-cor-observacao-row"><label className="agset-color-field">Cor da agenda<input type="color" value={form.cor || '#11c9d3'} onChange={(e) => setForm({ ...form, cor: e.target.value })} /></label><label>Observação<input value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} placeholder="Informações adicionais da agenda" /></label></div></div><div className="agset-modal-actions"><button type="button" className="agset-secondary" onClick={() => setModalAgenda(false)}>Cancelar</button><button type="submit" className="agset-primary" disabled={salvando}>{salvando ? 'Salvando…' : agendaEditando ? 'Salvar alterações' : 'Criar agenda e gerar horários'}</button></div></form></div>}

      {modalRelatorio && (
        <div
          className="agset-modal-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !exportando) {
              setModalRelatorio(false);
            }
          }}
        >
          <div className="agset-exp-modal">
            <h2>Relatório de agendamentos</h2>
            <p>
              Escolha o dia para gerar o relatório dos agendamentos.
            </p>

            <label className="agset-exp-label">
              Dia
              <DataExportBR value={diaExport} onChange={setDiaExport} />
            </label>

            {erroExport && (
              <div className="agset-exp-erro" role="alert">
                {erroExport}
              </div>
            )}

            <div className="agset-exp-acoes">
              <button
                type="button"
                className="agset-exp-sec"
                disabled={exportando}
                onClick={() => setModalRelatorio(false)}
              >
                Fechar
              </button>

              {podeBaixarDia && (
<button
                type="button"
                className="agset-exp-sec"
                disabled={exportando}
                onClick={() => exportarDia('txt')}
              >
                {exportando ? 'Gerando…' : 'Baixar TXT'}
              </button>
              )}

              <button
                type="button"
                className="agset-exp-pri"
                disabled={exportando}
                onClick={copiarRelatorioDia}
              >
                {exportando ? 'Copiando…' : 'Copiar resumo WhatsApp'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalExport && <div className="agset-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !exportando) setModalExport(false); }}><div className="agset-exp-modal"><h2>Baixar agendamentos do dia</h2><p>Setor {titulo} · todos os pacientes agendados no dia escolhido.</p><label className="agset-exp-label">Dia<DataExportBR value={diaExport} onChange={setDiaExport} /></label>{erroExport && <div className="agset-exp-erro" role="alert">{erroExport}</div>}<div className="agset-exp-acoes"><button type="button" className="agset-exp-sec" disabled={exportando} onClick={() => setModalExport(false)}>Fechar</button><button type="button" className="agset-exp-sec" disabled={exportando} onClick={() => exportarDia('pdf')}>{exportando ? 'Gerando…' : 'Baixar PDF'}</button><button type="button" className="agset-exp-pri" disabled={exportando} onClick={() => exportarDia('excel')}>{exportando ? 'Gerando…' : 'Baixar Excel'}</button></div></div></div>}
      {modalEspecialidade && <div className="agset-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setModalEspecialidade(false); }}><form className="agset-modal agset-small-modal" onSubmit={salvarEspecialidade}><div className="agset-modal-head"><div><h2>Nova especialidade · {titulo}</h2><p>Esta especialidade ficará vinculada somente a este setor.</p></div><button type="button" className="agset-icon-button" onClick={() => setModalEspecialidade(false)} aria-label="Fechar"><X size={18} /></button></div><label>Nome da especialidade<input autoFocus value={novaEspecialidade} onChange={(e) => setNovaEspecialidade(e.target.value)} placeholder="Ex.: Ortodontia" /></label><div className="agset-modal-actions"><button type="button" className="agset-secondary" onClick={() => setModalEspecialidade(false)}>Cancelar</button><button type="submit" className="agset-primary">Salvar especialidade</button></div></form></div>}
    </main>
  );
}
